import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { config } from '../lib/config';
import { identifyAdminUser, trackAdminEvent, useAdminClickAutocapture } from '../lib/analytics';
import CmsWorkspace from '../cms/CmsWorkspace';
import EventsWorkspace from '../events/EventsWorkspace';
import LocationCopyWorkspace from '../location-copy/LocationCopyWorkspace';
import MatchCopyWorkspace from '../match-copy/MatchCopyWorkspace';
import SportFormatAuditWorkspace from '../sport-format-audit/SportFormatAuditWorkspace';
import PlayModeCopyWorkspace from '../play-mode-copy/PlayModeCopyWorkspace';
import ReportsWorkspace from '../moderation/ReportsWorkspace';
import '../styles.admin.css';

const STANDALONE_ADMIN = import.meta.env.VITE_STANDALONE_ADMIN === 'true';

type TabKey =
  | 'settings'
  | 'reports'
  | 'users'
  | 'dashboard'
  | 'match_copy'
  | 'location_copy'
  | 'play_mode_copy'
  | 'sports'
  | 'sport_audit'
  | 'onboarding'
  | 'cms'
  | 'events';

type StatusTone = 'info' | 'success' | 'error' | 'warning';

type StatusMessage = {
  tone: StatusTone;
  text: string;
};

type AdminConsoleResponse<T> = { ok?: boolean; error?: string; value?: any; settings?: any } & T;

type FeedFilters = {
  nearbyRadiusKm: number;
  injectRecentDays: number;
  injectMaxItems: number;
  includeFollowedVenues: boolean;
  includeNearbyVenues: boolean;
  includeFollowedUsers: boolean;
  includeViewerParticipation: boolean;
};

type DebugFlags = {
  lobbyFeatureFlags: boolean;
};

type PushGeofenceSettings = {
  matchCreatedEnabled: boolean;
  matchCreatedRadiusKm: number;
  matchCreatedMaxAgeHours: number;
};

type FeatureFlagOverridesV1 = Record<string, boolean>;
type FeatureFlagRolloutMode = 'on' | 'off';
type FeatureFlagRolloutRule = { mode: FeatureFlagRolloutMode; percent: number };
type FeatureFlagSettingsV2 = {
  version: 2;
  defaults: Record<string, boolean>;
  rollouts: Record<string, FeatureFlagRolloutRule>;
  usersOn: Record<string, string[]>;
  usersOff: Record<string, string[]>;
  [key: string]: unknown;
};
type FeatureFlagSettingsValue = FeatureFlagOverridesV1 | FeatureFlagSettingsV2;
type FlagTriState = 'default' | 'on' | 'off';

type SportReleaseStage = 'hidden' | 'internal' | 'beta' | 'live';
type SportCatalogTargeting = { users: string[]; percent: number };
type SportCatalogGroupSettings = {
  label: string;
  subtitle: string;
  order: number;
  stage: SportReleaseStage;
  targeting: SportCatalogTargeting;
};
type SportCatalogSportSettings = {
  label: string;
  groupId: string;
  order: number;
  stage: SportReleaseStage;
  featured: boolean;
  internalNotes: string;
  launchNotes: string;
  targeting: SportCatalogTargeting;
};
type SportCatalogSettings = {
  version: 1;
  groups: Record<string, SportCatalogGroupSettings>;
  sports: Record<string, SportCatalogSportSettings>;
};

type DashboardResponse = {
  kpis: Record<string, number>;
  trendSignups: number[];
  trendJoins: number[];
  trendSessions: number[];
  recentUsers: Array<{ id: string; label: string; created_at: string }>;
  recentSessions: Array<{ id: string; status: string; created_at: string }>;
  recentMatches: Array<{ id: string; status: string; sport_id: string; created_at: string }>;
  funnel: {
    base: number;
    rows: Array<{ label: string; value: number; meta: string }>;
  };
  signals: Array<{ label: string; value: string; meta: string }>;
  insights: {
    sports: Array<{ label: string; value: string; meta?: string }>;
    venues: Array<{ label: string; value: string; meta?: string }>;
    recentVenues: Array<{ label: string; value: string; meta?: string }>;
    areas: Array<{ label: string; value: string; meta?: string }>;
  };
  warning?: string | null;
};

type UserIntelMatch = {
  id: string;
  sport_id: string | null;
  status: string | null;
  mode: string | null;
  created_at: string | null;
  joined_at?: string | null;
  venue_name: string | null;
};

type UserIntelRow = {
  id: string;
  email: string | null;
  phone: string | null;
  last_sign_in_at: string | null;
  confirmed_at: string | null;
  sign_in_provider: string | null;
  profile: Record<string, any>;
  profileCompleteness: number;
  preferredActivities: string[];
  homeZip: string | null;
  homeArea: string | null;
  communityStatus: Record<string, any> | null;
  independentLogsCount?: number;
  activityCount?: number;
  createdGamesCount: number;
  participatedGamesCount: number;
  sessionCount: number;
  lastCreatedGameAt: string | null;
  lastParticipatedGameAt: string | null;
  topCreatedSports: Array<{ label: string; value: string }>;
  topParticipatedSports: Array<{ label: string; value: string }>;
  recentCreatedGames: UserIntelMatch[];
  recentParticipatedGames: UserIntelMatch[];
  valueStage: string;
  valueSignals: string[];
  openQuestions: string[];
};

type UserIntelligenceValue = {
  windowDays: number;
  limit: number;
  overview: Record<string, number>;
  questions: Array<{ label: string; value: string; meta: string }>;
  insights: {
    preferredActivities: Array<{ label: string; value: string }>;
    zipCodes: Array<{ label: string; value: string }>;
    createdSports: Array<{ label: string; value: string }>;
    participatedSports: Array<{ label: string; value: string }>;
    valueStages: Array<{ label: string; value: string }>;
  };
  users: UserIntelRow[];
  warning?: string | null;
};

type UserIntelligenceResponse = {
  value: UserIntelligenceValue;
};

type ReportStatus = 'open' | 'triaged' | 'closed';
type ReportRow = {
  id: string;
  created_at: string;
  status: ReportStatus;
  entity_type: string | null;
  entity_id: string | null;
  reason: string | null;
  details: string | null;
  reporter_id: string | null;
  reported_user_id: string | null;
};

type ClientErrorStatus = 'open' | 'triaged' | 'closed';
type ClientErrorRow = {
  id: string;
  created_at: string;
  status: ClientErrorStatus;
  event_name: string;
  user_id: string | null;
  match_id: string | null;
  route: string | null;
  notification_type: string | null;
  error_message: string | null;
  error_code: string | null;
  error_stack: string | null;
  app_version: string | null;
  platform: string | null;
  notification_payload: any | null;
  params: any | null;
  build: any | null;
};

type AdminAccessRow = {
  user_id: string;
  email: string | null;
  username: string | null;
  full_name: string | null;
  created_at: string | null;
};

const ADMIN_TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'users', label: 'Users' },
  { key: 'cms', label: 'CMS' },
  { key: 'events', label: 'Events' },
  { key: 'reports', label: 'Reports' },
  { key: 'match_copy', label: 'Match copy' },
  { key: 'location_copy', label: 'Location copy' },
  { key: 'play_mode_copy', label: 'Play mode copy' },
  { key: 'sports', label: 'Sports' },
  { key: 'sport_audit', label: 'Sport audit' },
  { key: 'onboarding', label: 'Onboarding' },
  { key: 'settings', label: 'Settings' },
];

function formatDate(ts: string | null | undefined) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function AdminTabIcon({ tab }: { tab: TabKey }) {
  switch (tab) {
    case 'dashboard':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="5" rx="1.5" />
          <rect x="14" y="10" width="7" height="11" rx="1.5" />
          <rect x="3" y="12" width="7" height="9" rx="1.5" />
        </svg>
      );
    case 'users':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="9" cy="8.5" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M3.8 19.5c.8-3.2 2.5-4.8 5.2-4.8s4.4 1.6 5.2 4.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="16.8" cy="10" r="2.3" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M14.7 15.2c2.7.2 4.5 1.6 5.5 4.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'cms':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 4.5h8l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 6 19V6A1.5 1.5 0 0 1 7.5 4.5Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M15 4.5V9h4" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9 12h6M9 15.5h6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'events':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 4.5h10A2.5 2.5 0 0 1 19.5 7v10a2.5 2.5 0 0 1-2.5 2.5H7A2.5 2.5 0 0 1 4.5 17V7A2.5 2.5 0 0 1 7 4.5Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8.5 9.5h7M8.5 14.5h3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="16.5" cy="14.5" r="1.75" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case 'reports':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 20V5.5A1.5 1.5 0 0 1 6.5 4H16l3 3v13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M9 9.5h6M9 13h6M9 16.5h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'match_copy':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="5" y="4" width="11" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9 8.5h3.5M9 12h6M9 15.5h6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M16 8h3v12H8v-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'onboarding':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m12 3 1.9 4.6L18.5 9l-4.6 1.4L12 15l-1.9-4.6L5.5 9l4.6-1.4Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M18 15.5 19 18l2.5 1-2.5 1L18 22.5 17 20l-2.5-1 2.5-1Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      );
    case 'location_copy':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 20.5s-5.5-5.18-5.5-10A5.5 5.5 0 0 1 12 5a5.5 5.5 0 0 1 5.5 5.5c0 4.82-5.5 10-5.5 10Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="12" cy="10.5" r="2.1" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M4.5 20h15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'play_mode_copy':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4.5" y="4.5" width="15" height="15" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8.5 9.5h7M8.5 13h7M8.5 16.5h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="16.8" cy="16.4" r="1.7" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case 'sports':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6.5 6.5h11v11h-11Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9 12h6M12 9v6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M4.5 12h3M16.5 12h3M12 4.5v3M12 16.5v3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'sport_audit':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4.5" y="4.5" width="15" height="15" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 7.2v1.6M12 15.2v1.6M7.2 12h1.6M15.2 12h1.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'settings':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 3.5v2.2M12 18.3v2.2M20.5 12h-2.2M5.7 12H3.5M17.7 6.3l-1.6 1.6M7.9 16.1l-1.6 1.6M17.7 17.7l-1.6-1.6M7.9 7.9 6.3 6.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}

async function adminFetch<T>(session: Session, payload: Record<string, unknown>): Promise<AdminConsoleResponse<T>> {
  const url = `${config.supabaseUrl}/functions/v1/admin-console`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: config.supabaseAnonKey,
      authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as AdminConsoleResponse<T>;
  if (!res.ok) {
    throw new Error(body?.error || `Request failed (${res.status})`);
  }
  if (body?.error) {
    throw new Error(body.error);
  }
  return body;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge(base: unknown, override: unknown): unknown {
  if (!isObject(base) || !isObject(override)) return override ?? base;
  const out: Record<string, unknown> = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    out[key] = deepMerge(base[key], value);
  });
  return out;
}

function deepDiff(base: unknown, value: unknown): unknown | undefined {
  if (value === undefined) return undefined;
  if (!isObject(base) || !isObject(value)) return value === base ? undefined : value;
  const out: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(base), ...Object.keys(value)]);
  keys.forEach((key) => {
    const next = deepDiff(base[key], value[key]);
    if (next !== undefined) out[key] = next as unknown;
  });
  return Object.keys(out).length ? out : undefined;
}

function stableStringify(value: unknown) {
  const seen = new WeakSet();
  const sortKeys = (v: unknown): unknown => {
    if (!isObject(v)) return v;
    if (seen.has(v as object)) return v;
    seen.add(v as object);
    const out: Record<string, unknown> = {};
    Object.keys(v)
      .sort()
      .forEach((key) => {
        out[key] = sortKeys(v[key]);
      });
    return out;
  };
  return JSON.stringify(sortKeys(value), null, 2);
}

function getPathValue(source: unknown, path: string): unknown {
  const parts = path.split('.').filter(Boolean);
  let cur: unknown = source;
  for (const part of parts) {
    if (!isObject(cur)) return undefined;
    cur = cur[part];
  }
  return cur;
}

function setPathValue(target: Record<string, unknown>, path: string, value: string) {
  const parts = path.split('.').filter(Boolean);
  if (parts.length === 0) return;
  let cur: Record<string, unknown> = target;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    const next = cur[key];
    if (!isObject(next)) cur[key] = {};
    cur = cur[key] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]] = value;
}

function deletePathValue(target: Record<string, unknown>, path: string) {
  const parts = path.split('.').filter(Boolean);
  if (parts.length === 0) return;
  const stack: Array<{ parent: Record<string, unknown>; key: string }> = [];
  let cur: Record<string, unknown> = target;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    const next = cur[key];
    if (!isObject(next)) return;
    stack.push({ parent: cur, key });
    cur = next;
  }
  const leafKey = parts[parts.length - 1];
  if (!Object.prototype.hasOwnProperty.call(cur, leafKey)) return;
  delete cur[leafKey];
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const { parent, key } = stack[i];
    const node = parent[key];
    if (isObject(node) && Object.keys(node).length === 0) delete parent[key];
    else break;
  }
}

function normalizeKey(value: unknown): string {
  if (typeof value !== 'string') return 'unknown';
  const v = value.trim();
  return v.length ? v : 'unknown';
}

function takeTop(map: Map<string, number>, limit = 6): Array<{ key: string; count: number }> {
  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([key, count]) => ({ key, count }));
}

function pickBuildChannel(build: any): string {
  const v =
    build?.buildInfo?.channel ??
    build?.buildInfo?.releaseChannel ??
    build?.channel ??
    build?.releaseChannel ??
    null;
  return normalizeKey(v);
}

function summarizeClientErrors(rows: ClientErrorRow[]) {
  const byAppVersion = new Map<string, number>();
  const byChannel = new Map<string, number>();
  const byPlatform = new Map<string, number>();
  const byNotificationType = new Map<string, number>();
  const byErrorCode = new Map<string, number>();
  let latestAt: Date | null = null;

  for (const r of rows) {
    const app = normalizeKey(r.app_version);
    byAppVersion.set(app, (byAppVersion.get(app) ?? 0) + 1);
    const chan = pickBuildChannel(r.build);
    byChannel.set(chan, (byChannel.get(chan) ?? 0) + 1);
    const plat = normalizeKey(r.platform);
    byPlatform.set(plat, (byPlatform.get(plat) ?? 0) + 1);
    const nt = normalizeKey(r.notification_type);
    byNotificationType.set(nt, (byNotificationType.get(nt) ?? 0) + 1);
    const code = normalizeKey(r.error_code);
    byErrorCode.set(code, (byErrorCode.get(code) ?? 0) + 1);
    const d = r.created_at ? new Date(r.created_at) : null;
    if (d && !Number.isNaN(d.getTime())) {
      if (!latestAt || d.getTime() > latestAt.getTime()) latestAt = d;
    }
  }

  return {
    latestAt,
    filters: 'event=push_open_match_error',
    byAppVersion: takeTop(byAppVersion),
    byChannel: takeTop(byChannel),
    byPlatform: takeTop(byPlatform),
    byNotificationType: takeTop(byNotificationType),
    byErrorCode: takeTop(byErrorCode),
  };
}

function SummaryRow({ title, rows }: { title: string; rows: Array<{ key: string; count: number }> }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="admin-row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
      <div className="admin-muted" style={{ minWidth: 140 }}>{title}</div>
      <div className="admin-row" style={{ flexWrap: 'wrap', gap: 8, justifyContent: 'flex-start' }}>
        {rows.map((r) => (
          <span key={`${title}:${r.key}`} className="admin-chip">
            <span className="admin-mono" style={{ marginRight: 6 }}>{r.count}</span>
            {r.key}
          </span>
        ))}
      </div>
    </div>
  );
}

function formatTemplate(template: string, vars?: Record<string, string | number | null | undefined>) {
  if (!vars) return template;
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, key) => {
    const value = vars[key];
    if (value === null || value === undefined) return match;
    return String(value);
  });
}

const DEFAULT_FEED_FILTERS: FeedFilters = {
  nearbyRadiusKm: 32.2,
  injectRecentDays: 7,
  injectMaxItems: 10,
  includeFollowedVenues: true,
  includeNearbyVenues: true,
  includeFollowedUsers: true,
  includeViewerParticipation: true,
};

const DEFAULT_DEBUG_FLAGS: DebugFlags = {
  lobbyFeatureFlags: false,
};

const DEFAULT_PUSH_GEOFENCE: PushGeofenceSettings = {
  matchCreatedEnabled: true,
  matchCreatedRadiusKm: 80,
  matchCreatedMaxAgeHours: 72,
};

// Keep aligned with `supabase/functions/admin-console/index.ts` `FEATURE_FLAG_KEYS`.
const FEATURE_FLAG_KEYS = [
  'HOSTED_EVENTS_V1',
  'SESSION_V1',
  'SESSION_INTENT_V1',
  'SESSION_LINEUP_MODES_V1',
  'LIVE_ACTIVITY_DEV_V1',
  'LIVE_ACTIVITY_PROD_V1',
  'MODE_TEAMS_V1',
  'MODE_FIXED_TEAMS_V1',
  'MODE_WINNER_STAYS_V1',
  'MODE_AUTO_BALANCE_V1',
  'MODE_BRACKET_V1',
  'MODE_TOURNAMENT_V1',
  'SESSION_PLAY_NEXT_V1',
  'PLAY_NEXT_LOBBY_READY_V1',
  'MATCH_EXPERIENCE_NEXT_GAME_V1',
  'UP_NEXT_HIDDEN_CORE_V1',
  'ACTIVITY_RESULT_UNITS_V1',
  'LOBBY_UP_NEXT_EXPERIMENT_V1',
  'LINEUP_BUILDER_V1',
  'FEED_PARTICIPANT_STRIP_V1',
  'FLOW_WHATS_NEXT_V1',
  'E2E_RESULT_SUMMARY_V1',
  'SESSION_DEFAULT_LOBBY_TOGGLE_V1',
  'LOBBY_PLAYERS_AND_NEXT_GAMES_V1',
  'LOBBY_UX_POLISH_V1',
  'LOBBY_MINIMAL_V1',
  'LOBBY_MINIMAL_HARD_V1',
  'LOBBY_INLINE_TEAM_BUILDER_V1',
  'LOBBY_AESTHETIC_V1',
  'LOBBY_TEAMS_DEFAULT_V1',
  'LOBBY_VIBE_V1',
  'VENUE_CLAIMING_V1',
  'REUSABLE_TEAMS_COMMUNITY_V1',
  'WORKOUT_ROUTINES_V1',
  'SOLO_FIRST_EXPERIENCE_V1',
  'MAP_DISCOVERY_AUGMENTATION_V1',
  'MAPBOX_NATIVE_PILOT_V1',
  'AREA_PLAY_V1',
  'CONVERSATION_V1',
  'CONVERSATION_CLOUD_STT_V1',
  'CHALLENGES_V1',
] as const;

const FEATURED_FLAG_KEYS = [
  'HOSTED_EVENTS_V1',
  'SESSION_V1',
  'LIVE_ACTIVITY_DEV_V1',
  'LIVE_ACTIVITY_PROD_V1',
] as const;

const FEATURE_FLAG_DESCRIPTIONS: Record<(typeof FEATURE_FLAG_KEYS)[number], string> = {
  HOSTED_EVENTS_V1: 'Enables venue-first hosted events, including hosted trivia and judged challenges.',
  SESSION_V1: 'Enables the Session v1 flow (lobby + game flow refactor).',
  SESSION_INTENT_V1: 'Adds an “intent” step to Session v1.',
  SESSION_LINEUP_MODES_V1: 'Enables lineup/team-building modes inside Session v1.',
  LIVE_ACTIVITY_DEV_V1: 'Enables Live Activity integration for dev/internal builds.',
  LIVE_ACTIVITY_PROD_V1: 'Enables Live Activity integration for production builds.',
  MODE_TEAMS_V1: 'Shows the Teams option in the create-flow play mode picker. Keep off while fixed-team setup is hidden.',
  MODE_FIXED_TEAMS_V1: 'Shows/enables the “Fixed teams” play mode.',
  MODE_WINNER_STAYS_V1: 'Shows/enables the “Winner stays” play mode.',
  MODE_AUTO_BALANCE_V1: 'Shows/enables the “Auto-balance” play mode.',
  MODE_BRACKET_V1: 'Shows/enables the “Bracket” play mode.',
  MODE_TOURNAMENT_V1: 'Controls whether Tournament appears in the in-app Play Mode menu.',
  SESSION_PLAY_NEXT_V1: 'Enables the “Play Next” flow (challengers queue / next-up selection).',
  PLAY_NEXT_LOBBY_READY_V1: 'Adds a “Ready” gate to the Play Next lobby.',
  MATCH_EXPERIENCE_NEXT_GAME_V1: 'Enables “Next game” match experience UI.',
  UP_NEXT_HIDDEN_CORE_V1: 'Enables core “Up Next” logic/UI.',
  ACTIVITY_RESULT_UNITS_V1: 'Keeps Play Next inside one activity run and selects setup or direct continuation from the versioned activity definition.',
  LOBBY_UP_NEXT_EXPERIMENT_V1: 'Enables the “Up Next” experiment in the lobby UI.',
  LINEUP_BUILDER_V1: 'Enables the lineup builder UI.',
  FEED_PARTICIPANT_STRIP_V1: 'Shows the participant strip on feed items.',
  FLOW_WHATS_NEXT_V1: 'Enables the “What’s next?” post-round flow.',
  E2E_RESULT_SUMMARY_V1: 'Enables the end-to-end results summary screen after a match.',
  SESSION_DEFAULT_LOBBY_TOGGLE_V1: 'Uses the Session v1 lobby as the default lobby experience (when possible).',
  LOBBY_PLAYERS_AND_NEXT_GAMES_V1: 'Lobby UI: players list + next games section.',
  LOBBY_UX_POLISH_V1: 'Lobby UI: polish tweaks bundle.',
  LOBBY_MINIMAL_V1: 'Lobby UI: minimal style.',
  LOBBY_MINIMAL_HARD_V1: 'Lobby UI: stricter minimal layout.',
  LOBBY_INLINE_TEAM_BUILDER_V1: 'Lobby UI: inline team builder.',
  LOBBY_AESTHETIC_V1: 'Lobby UI: aesthetic/visual treatment.',
  LOBBY_TEAMS_DEFAULT_V1: 'Lobby UI: default teams layout.',
  LOBBY_VIBE_V1: 'Lobby UI: vibe/ambient treatment.',
  VENUE_CLAIMING_V1: 'Enables the venue claiming flow.',
  REUSABLE_TEAMS_COMMUNITY_V1: 'Enables reusable teams in Community (create/manage teams, invites, and team roster match invites).',
  WORKOUT_ROUTINES_V1: 'Enables personal and public workout routines under You, plus save/load hooks in weightlifting.',
  SOLO_FIRST_EXPERIENCE_V1: 'Makes solo/session tracking the first-class create-flow path for supported sports while keeping competitive formats available.',
  MAP_DISCOVERY_AUGMENTATION_V1: 'Map discovery: enables augmentation of venue discovery with additional ranking/source inputs before broader map rollouts.',
  AREA_PLAY_V1: 'Area Play preview: public area map and weekly standings. Keep off until native acceptance.',
  CONVERSATION_V1: 'Enables Ask RVLRY for solo logging, correction and Undo. Off until native acceptance.',
  CONVERSATION_CLOUD_STT_V1: 'Allows explicitly chosen cloud transcription within the shared monthly AI budget.',
  CHALLENGES_V1: 'Enables community Challenges. Off for launch; activity invitations remain available.',
  MAPBOX_NATIVE_PILOT_V1: 'Enables the native Mapbox pilot gate for interactive maps. Current scaffold still falls back to the existing renderer until the implementation lands.',
};

function groupForFlagKey(key: string) {
  if (key.startsWith('HOSTED_EVENTS_')) return 'Venues';
  if (key.startsWith('MAPBOX_')) return 'Maps';
  if (key.startsWith('VENUE_')) return 'Venues';
  if (key.startsWith('REUSABLE_TEAMS_')) return 'Teams';
  if (key.startsWith('WORKOUT_ROUTINES_')) return 'Workout';
  if (key.startsWith('SOLO_FIRST_')) return 'Sessions';
  if (key.startsWith('SESSION_') || key.startsWith('PLAY_NEXT_')) return 'Sessions';
  if (key.startsWith('LOBBY_')) return 'Lobby';
  if (key.startsWith('MODE_')) return 'Modes';
  if (key.startsWith('LIVE_ACTIVITY_')) return 'Live Activity';
  if (key.startsWith('FEED_')) return 'Feed';
  if (key.startsWith('FLOW_') || key.startsWith('E2E_')) return 'Flow';
  if (key.startsWith('MATCH_EXPERIENCE_') || key.startsWith('UP_NEXT_')) return 'Match Experience';
  if (key.startsWith('ACTIVITY_RESULT_')) return 'Match Experience';
  if (key.startsWith('LINEUP_')) return 'Lineup';
  return 'Other';
}

function emptyFeatureFlagSettings(): FeatureFlagSettingsV2 {
  return { version: 2, defaults: {}, rollouts: {}, usersOn: {}, usersOff: {} };
}

function normalizeUsername(value: string): string | null {
  const raw = value.trim().replace(/^@+/, '').toLowerCase();
  if (!raw) return null;
  if (!/^[a-z0-9_.-]{3,24}$/.test(raw)) return null;
  return raw;
}

function parseIdentifierList(input: unknown): string[] {
  const text = Array.isArray(input) ? input.join('\n') : String(input ?? '');
  const tokens = text.split(/[\s,]+/g).map((t) => t.trim()).filter(Boolean);
  const out: string[] = [];
  const seen = new Set<string>();
  const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  for (const tok of tokens) {
    const low = tok.toLowerCase();
    if (uuidRe.test(low)) {
      if (seen.has(low)) continue;
      seen.add(low);
      out.push(low);
      continue;
    }
    const uname = normalizeUsername(tok);
    if (!uname) continue;
    if (seen.has(uname)) continue;
    seen.add(uname);
    out.push(uname);
  }
  return out;
}

function normalizeFeatureFlagSettings(input: unknown): FeatureFlagSettingsV2 {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return emptyFeatureFlagSettings();
  const src = input as Record<string, unknown>;
  if ((src as any).version === 2) {
    const defaultsSrc = (src.defaults && typeof src.defaults === 'object' && !Array.isArray(src.defaults))
      ? (src.defaults as Record<string, unknown>)
      : {};
    const rolloutsSrc = (src.rollouts && typeof src.rollouts === 'object' && !Array.isArray(src.rollouts))
      ? (src.rollouts as Record<string, unknown>)
      : {};
    const usersOnSrc = (src.usersOn && typeof src.usersOn === 'object' && !Array.isArray(src.usersOn))
      ? (src.usersOn as Record<string, unknown>)
      : {};
    const usersOffSrc = (src.usersOff && typeof src.usersOff === 'object' && !Array.isArray(src.usersOff))
      ? (src.usersOff as Record<string, unknown>)
      : {};

    const defaults: Record<string, boolean> = {};
    const rollouts: Record<string, FeatureFlagRolloutRule> = {};
    const usersOn: Record<string, string[]> = {};
    const usersOff: Record<string, string[]> = {};

    FEATURE_FLAG_KEYS.forEach((key) => {
      const v = defaultsSrc[key];
      if (typeof v === 'boolean') defaults[key] = v;

      const ruleRaw = rolloutsSrc[key];
      if (ruleRaw && typeof ruleRaw === 'object' && !Array.isArray(ruleRaw)) {
        const r = ruleRaw as any;
        const mode = r.mode === 'off' ? 'off' : r.mode === 'on' ? 'on' : null;
        const percentNum = typeof r.percent === 'number' ? r.percent : Number(r.percent);
        const percent = Number.isFinite(percentNum) ? Math.max(0, Math.min(100, percentNum)) : 0;
        if (mode && percent > 0) rollouts[key] = { mode, percent: Math.round(percent * 100) / 100 };
      }

      const onList = parseIdentifierList(usersOnSrc[key]);
      if (onList.length) usersOn[key] = onList;

      const offList = parseIdentifierList(usersOffSrc[key]);
      if (offList.length) usersOff[key] = offList;
    });

    const out: FeatureFlagSettingsV2 = { version: 2, defaults, rollouts, usersOn, usersOff };
    FEATURE_FLAG_KEYS.forEach((key) => {
      const v = defaults[key];
      if (typeof v === 'boolean') out[key] = v;
    });
    return out;
  }

  // v1 back-compat: { [flagKey]: boolean }
  const defaults: Record<string, boolean> = {};
  FEATURE_FLAG_KEYS.forEach((key) => {
    const v = src[key];
    if (typeof v === 'boolean') defaults[key] = v;
  });
  const out: FeatureFlagSettingsV2 = { version: 2, defaults, rollouts: {}, usersOn: {}, usersOff: {} };
  FEATURE_FLAG_KEYS.forEach((key) => {
    const v = defaults[key];
    if (typeof v === 'boolean') out[key] = v;
  });
  return out;
}

function getFlagTriState(flags: FeatureFlagSettingsV2, key: string): FlagTriState {
  if (!Object.prototype.hasOwnProperty.call(flags.defaults, key)) return 'default';
  return flags.defaults[key] ? 'on' : 'off';
}

function applyFlagTriState(flags: FeatureFlagSettingsV2, key: string, state: FlagTriState): FeatureFlagSettingsV2 {
  const nextDefaults: Record<string, boolean> = { ...flags.defaults };
  if (state === 'default') delete nextDefaults[key];
  else nextDefaults[key] = state === 'on';
  const out: FeatureFlagSettingsV2 = { ...flags, defaults: nextDefaults };
  FEATURE_FLAG_KEYS.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(out.defaults, k)) out[k] = out.defaults[k];
    else delete (out as any)[k];
  });
  return out;
}

function applyFlagUserTargets(
  flags: FeatureFlagSettingsV2,
  key: string,
  kind: 'usersOn' | 'usersOff',
  ids: string[],
): FeatureFlagSettingsV2 {
  const next = { ...flags[kind] };
  if (!ids.length) delete next[key];
  else next[key] = ids;
  return { ...flags, [kind]: next };
}

function applyFlagRollout(
  flags: FeatureFlagSettingsV2,
  key: string,
  rule: FeatureFlagRolloutRule | null,
): FeatureFlagSettingsV2 {
  const next = { ...flags.rollouts };
  if (!rule || !rule.percent || rule.percent <= 0) delete next[key];
  else next[key] = { mode: rule.mode, percent: Math.round(Math.max(0, Math.min(100, rule.percent)) * 100) / 100 };
  return { ...flags, rollouts: next };
}

function countFlagOverrides(flags: FeatureFlagSettingsV2): number {
  const defaults = Object.keys(flags.defaults).length;
  const rollouts = Object.keys(flags.rollouts).length;
  const userOn = Object.values(flags.usersOn).reduce((n, list) => n + (Array.isArray(list) ? list.length : 0), 0);
  const userOff = Object.values(flags.usersOff).reduce((n, list) => n + (Array.isArray(list) ? list.length : 0), 0);
  return defaults + rollouts + userOn + userOff;
}

const SPORT_RELEASE_STAGES: SportReleaseStage[] = ['hidden', 'internal', 'beta', 'live'];

const SPORT_GROUPS_CATALOG = [
  {
    id: 'court-field',
    label: 'Court & Field Sports',
    subtitle: 'Courts, turf, walls, and field-sport staples.',
    sports: [
      ['pickleball', 'Pickleball'],
      ['tennis', 'Tennis'],
      ['basketball', 'Basketball'],
      ['soccer', 'Soccer'],
      ['volleyball', 'Volleyball'],
      ['badminton', 'Badminton'],
      ['spikeball', 'Spikeball'],
      ['street hockey', 'Street Hockey'],
      ['lacrosse', 'Lacrosse'],
      ['flag football', 'Flag Football'],
      ['kickball', 'Kickball'],
      ['racquetball', 'Racquetball'],
      ['squash', 'Squash'],
      ['disc golf', 'Disc Golf'],
      ['dodgeball', 'Dodgeball'],
    ],
  },
  {
    id: 'table-precision',
    label: 'Precision & Table Games',
    subtitle: 'Precision reps, board games, and bar-side skill.',
    sports: [
      ['ping pong', 'Ping Pong'],
      ['pool', 'Pool'],
      ['darts', 'Darts'],
      ['foosball', 'Foosball'],
      ['air hockey', 'Air Hockey'],
      ['bowling', 'Bowling'],
      ['pinball', 'Pinball'],
      ['shuffleboard', 'Shuffleboard'],
      ['beer pong', 'Beer Pong'],
      ['jenga', 'Jenga'],
      ['checkers', 'Checkers'],
      ['chess', 'Chess'],
      ['tic tac toe', 'Tic Tac Toe'],
    ],
  },
  {
    id: 'park-backyard',
    label: 'Park & Backyard Games',
    subtitle: 'Easygoing outdoor classics and cookout staples.',
    sports: [
      ['cornhole', 'Cornhole'],
      ['bocce', 'Bocce'],
      ['ladder toss', 'Ladder Toss'],
      ['horseshoes', 'Horseshoes'],
    ],
  },
  {
    id: 'gym-performance',
    label: 'Gym Performance',
    subtitle: 'Gym floors, strength circuits, and performance reps.',
    sports: [
      ['weightlifting', 'Weightlifting'],
      ['crossfit', 'CrossFit'],
      ['hyrox', 'HYROX'],
    ],
  },
  {
    id: 'foot-race',
    label: 'Foot Races',
    subtitle: 'Short-burst speed and mile markers.',
    sports: [
      ['sprint 100-400m', 'Sprint (100-400m)'],
      ['1 mile', '1 Mile Run'],
    ],
  },
  {
    id: 'trick-style',
    label: 'Action Sports',
    subtitle: 'Movement, timing, style, and clean execution.',
    sports: [
      ['skateboarding', 'Skateboarding'],
      ['bmx flatground', 'BMX'],
      ['scooter', 'Scooter'],
      ['inline tricks', 'Blading'],
      ['rock climbing', 'Rock Climbing'],
      ['parkour', 'Parkour'],
      ['chase tag', 'Chase Tag'],
      ['breakdancing', 'Breakdancing'],
    ],
  },
  {
    id: 'combat',
    label: 'Combat Sports',
    subtitle: 'Friendly rounds, grapples, and controlled head-to-heads.',
    sports: [
      ['friendly sparring', 'Sparring'],
      ['arm wrestle', 'Arm Wrestling'],
    ],
  },
] as const;

const SPORT_GROUP_IDS = SPORT_GROUPS_CATALOG.map((group) => group.id);
const SPORT_IDS = SPORT_GROUPS_CATALOG.flatMap((group) => group.sports.map(([id]) => id));
const DEFAULT_HIDDEN_SPORT_IDS = new Set([
  'breakdancing',
  'tic tac toe',
  'checkers',
  'chess',
  'parkour',
  'chase tag',
  'rock climbing',
  'crossfit',
  'hyrox',
]);

function emptySportCatalogSettings(): SportCatalogSettings {
  const groups: Record<string, SportCatalogGroupSettings> = {};
  const sports: Record<string, SportCatalogSportSettings> = {};
  SPORT_GROUPS_CATALOG.forEach((group, groupIndex) => {
    groups[group.id] = {
      label: group.label,
      subtitle: group.subtitle,
      order: groupIndex,
      stage: group.id === 'foot-race' ? 'hidden' : 'live',
      targeting: { users: [], percent: 0 },
    };
    group.sports.forEach(([sportId, label], sportIndex) => {
      sports[sportId] = {
        label,
        groupId: group.id,
        order: sportIndex,
        stage: DEFAULT_HIDDEN_SPORT_IDS.has(sportId) ? 'hidden' : 'live',
        featured: false,
        internalNotes: '',
        launchNotes: '',
        targeting: { users: [], percent: 0 },
      };
    });
  });
  return { version: 1, groups, sports };
}

function normalizeSportTargeting(input: unknown): SportCatalogTargeting {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? (input as Record<string, unknown>) : {};
  const percentRaw = typeof src.percent === 'number' ? src.percent : Number(src.percent);
  const percent = Number.isFinite(percentRaw) ? Math.max(0, Math.min(100, percentRaw)) : 0;
  return {
    users: parseIdentifierList(src.users),
    percent: Math.round(percent * 100) / 100,
  };
}

function normalizeSportStage(input: unknown, fallback: SportReleaseStage): SportReleaseStage {
  return SPORT_RELEASE_STAGES.includes(input as SportReleaseStage) ? (input as SportReleaseStage) : fallback;
}

function normalizeSportCatalogSettings(input: unknown): SportCatalogSettings {
  const defaults = emptySportCatalogSettings();
  const src = input && typeof input === 'object' && !Array.isArray(input) ? (input as Record<string, unknown>) : {};
  const groupsRaw = src.groups && typeof src.groups === 'object' && !Array.isArray(src.groups) ? (src.groups as Record<string, unknown>) : {};
  const sportsRaw = src.sports && typeof src.sports === 'object' && !Array.isArray(src.sports) ? (src.sports as Record<string, unknown>) : {};
  const groups: Record<string, SportCatalogGroupSettings> = {};
  const sports: Record<string, SportCatalogSportSettings> = {};

  SPORT_GROUPS_CATALOG.forEach((group) => {
    const fallback = defaults.groups[group.id];
    const raw = groupsRaw[group.id] && typeof groupsRaw[group.id] === 'object' && !Array.isArray(groupsRaw[group.id])
      ? (groupsRaw[group.id] as Record<string, unknown>)
      : {};
    groups[group.id] = {
      label: typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : fallback.label,
      subtitle: typeof raw.subtitle === 'string' && raw.subtitle.trim() ? raw.subtitle.trim() : fallback.subtitle,
      order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : fallback.order,
      stage: normalizeSportStage(raw.stage, fallback.stage),
      targeting: normalizeSportTargeting(raw.targeting),
    };
  });

  SPORT_GROUPS_CATALOG.forEach((group) => {
    group.sports.forEach(([sportId]) => {
      const fallback = defaults.sports[sportId];
      const raw = sportsRaw[sportId] && typeof sportsRaw[sportId] === 'object' && !Array.isArray(sportsRaw[sportId])
        ? (sportsRaw[sportId] as Record<string, unknown>)
        : {};
      const rawGroup = typeof raw.groupId === 'string' ? raw.groupId : fallback.groupId;
      sports[sportId] = {
        label: typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : fallback.label,
        groupId: SPORT_GROUP_IDS.includes(rawGroup as any) ? rawGroup : fallback.groupId,
        order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : fallback.order,
        stage: normalizeSportStage(raw.stage, fallback.stage),
        featured: typeof raw.featured === 'boolean' ? raw.featured : fallback.featured,
        internalNotes: typeof raw.internalNotes === 'string' ? raw.internalNotes : '',
        launchNotes: typeof raw.launchNotes === 'string' ? raw.launchNotes : '',
        targeting: normalizeSportTargeting(raw.targeting),
      };
    });
  });

  return { version: 1, groups, sports };
}

function countSportsByStage(catalog: SportCatalogSettings, stage: SportReleaseStage) {
  return SPORT_IDS.reduce((count, sportId) => count + (catalog.sports[sportId]?.stage === stage ? 1 : 0), 0);
}

// Keep aligned with `lib/api/matchExperienceCopy.ts` defaults.
const DEFAULT_MATCH_EXPERIENCE_COPY = {
  stepper: {
    heading: 'Match flow',
    labels: {
      lobby: 'Lobby',
      game_on: 'Play On',
      round_complete: 'Round Over',
      series_decision: 'What Next',
      loser_challenge: 'Bonus Puzzle',
      post_match: 'Results',
    },
  },
  sheets: {
    pointsInfo: {
      title: 'Points & leaderboard',
      bodyHtml:
        '<p><strong>The venue leaderboard is mostly wins.</strong></p>' +
        '<p>Your venue score is <strong>Wins × {winPoints}</strong> plus a small <strong>Upset Bonus</strong> when you beat a stronger opponent.</p>' +
        '<h3>What you earn</h3>' +
        '<ul>' +
        '<li><strong>Win:</strong> +{winPoints} points</li>' +
        '<li><strong>Upset Bonus:</strong> +0 to +5 points (only on wins)</li>' +
        '<li><strong>Loss / Tie:</strong> +0 points</li>' +
        '</ul>' +
        '<h3>Upset Bonus (0–5)</h3>' +
        '<p>Based on how much stronger the opponent is (using our internal skill rating). Bigger upsets earn a bigger bonus.</p>' +
        '<ul>' +
        '<li>Opponent is +0–99 rating: +0</li>' +
        '<li>Opponent is +100–199 rating: +1</li>' +
        '<li>Opponent is +200–299 rating: +2</li>' +
        '<li>Opponent is +300–399 rating: +3</li>' +
        '<li>Opponent is +400–499 rating: +4</li>' +
        '<li>Opponent is +500+ rating: +5</li>' +
        '</ul>' +
        '<h3>How ranking works</h3>' +
        '<p>We sort by venue score first. If two players have the same score, the upset bonus helps break ties.</p>' +
        '<hr />' +
        '<p><small>Tip: Venue points are separate for each sport and venue.</small></p>',
    },
  },
  content: {
    lobby: {
      participantsTitle: 'Participants',
      gymActivity: {
        activityTitle: 'Activity',
        liftTitle: 'Lift',
        optionalSubtitle: 'Choose a lift, workout, or station.',
        liftSubtitle: 'Choose the lift for this challenge.',
        selectedLockedSubtitle: 'The activity is set for this game.',
        selectedLiftLockedSubtitle: 'The lift is set for this challenge.',
        freeFlowLockedSubtitle: 'This game is running free flow.',
        noLiftLockedSubtitle: 'Choose a lift to get started.',
        freeFlowTitle: 'Free flow',
        chooseLiftTitle: 'Choose a lift',
        selectedHintEditable: 'Tap to change',
        selectedHintLocked: 'Locked in for this game',
        emptyOptionalHint: 'Choose one now, or leave it open.',
        emptyLiftHint: 'Choose a lift to start.',
        pickerTitle: 'Choose activity',
        pickerLiftTitle: 'Choose lift',
        pickerSubtitle: 'Choose a lift, workout, or station, or leave it open.',
        pickerLiftSubtitle: 'Choose the lift for this challenge.',
        freeFlowOptionTitle: 'Free flow',
        freeFlowOptionBody: 'No activity selected yet.',
        customActivityBody: 'Save this game with a custom activity name.',
        activityOptionBody: 'Use for this game.',
        liftOptionBody: 'Use for this challenge.',
      },
      hostGuidance: {
        sessionLockedTeamsSet: "Teams are set. Start when everyone's ready.",
        sessionLocked: "Pick sides for rivals, then tap Start when everyone's ready.",
        default: "Invite players, then tap Start when everyone's here.",
      },
      participantGuidance: {
        joinSession: 'Tap Join Game below to play. The controller will start when ready.',
        join: 'Tap Join Game below to play! The host will add teammates or start when ready.',
        getNextSession: 'Tap Get Next below to be considered. The controller picks who plays next.',
        getNext: 'Tap Get Next below to join the challengers list. The host picks who plays next.',
        alreadyJoiningSession: 'Players are already joining. Choose a side to hop into the game.',
        alreadyJoining: 'The host already has a challenger. Choose a side to hop into the game.',
      },
    },
    loserChallenge: {
      modal: {
        title: 'Loser challenge',
        body: 'You lost this series. Choose to accept the loss or fire the bonus puzzle.',
      },
    },
  },
  controls: {
    stepTitles: {
      lobby: 'LOBBY',
      game_on: 'GAME ON',
      round_complete: 'GAME OVER',
      series_decision: "WHAT'S NEXT?",
      loser_challenge: 'BONUS PUZZLE',
      post_match: 'WRAP-UP',
    },
    resultButtons: {
      title: 'WHO WON THIS ROUND?',
      subtitle: 'Press and hold to confirm.',
      tieLabel: 'Tie',
      accessibilityHint: 'Press and hold to confirm and end the round.',
    },
    teamSelect: { title: 'PICK A TEAM', subtitle: 'Choose a side to continue.' },
    wrapCloseOnly: { title: 'WRAP-UP', subtitle: 'Game finished.', closeCta: 'CLOSE' },
    playerActions: { title: 'PLAYER ACTIONS', subtitleLobbyWait: 'Waiting for {controller} to set teams and start round.' },
    bonusDecision: {
      title: 'BONUS PUZZLE DECISION',
      subtitleHost: 'Accept defeat or trigger the bonus puzzle before time runs out.',
      subtitleWait: 'Waiting for the losing side to decide.',
      declineCta: 'TAKE THE L',
      acceptCta: 'CHALLENGE IT',
      closeCta: 'CLOSE',
    },
    roundCompleteGate: {
      title: 'GAME OVER',
      subtitleHost: 'Add a final score or tap NEXT to continue.',
      subtitleWait: 'Waiting for {host} to add a score or tap NEXT.',
      addScoreCta: 'ADD SCORE',
      nextCta: 'NEXT',
      closeCta: 'CLOSE',
    },
    whatsNextRoundCompleteHost: { title: "WHAT'S NEXT?", subtitle: 'Run it back or finish.' },
    recapFooter: {
      title: 'RECAP',
      subtitle: 'Finalize and share the game results!',
      addPhotosCta: 'ADD PHOTOS',
      addPhotosLoading: 'UPLOADING…',
      shareCta: 'SHARE RECAP',
    },
    optionsSheet: { title: 'Additional Options', closeCta: 'Close' },
    hostSection: {
      waitingTitle: "Who's playing?",
      waitingSubtitle: 'Invite players, then pick sides to start.',
      pickSidesSubtitle: 'Pick sides to start.',
      readySubtitle: 'Start game when ready!',
    },
    entryCta: {
      joinTitle: 'JOIN',
      getNextTitle: 'GET NEXT',
      joinCta: 'Join game',
      joinHint: 'Join to pick a side.',
      leaveHint: 'Tap again to leave the list.',
      hostPicksHint: 'Host picks who plays next.',
    },
    leaveTeam: { label: 'Leave team' },
  },
  actions: {
    start_match: { label: 'Start', loadingLabel: 'Starting…' },
    cancel_match: { label: 'Cancel Game', loadingLabel: 'Cancelling…' },
    lock_teams: { label: 'Lock Teams', loadingLabel: 'Locking…' },
    abandon_match: { label: 'Cancel', loadingLabel: 'Cancelling…' },
    submit_result_a: { label: 'Win: Light', loadingLabel: 'Publishing…' },
    submit_result_b: { label: 'Win: Dark', loadingLabel: 'Publishing…' },
    submit_tie: { label: 'Declare Tie', loadingLabel: 'Publishing…' },
    end_practice: { label: 'Finish Challenge', loadingLabel: 'Ending…' },
    finish_match: { label: 'Finish', loadingLabel: 'Finishing…' },
    start_rematch: { label: 'Rematch', loadingLabel: 'Starting…' },
    join_match: { label: 'Join Game', loadingLabel: 'Joining…' },
    leave_match: { label: 'Leave Game', loadingLabel: 'Leaving…' },
    leave_team: { label: 'Leave Team', loadingLabel: 'Leaving…' },
    invite_players: { label: 'Invite Players', loadingLabel: 'Inviting…' },
    get_directions: { label: 'Directions' },
    show_qr: { label: 'QR Join' },
    join_team: { label: 'Join Team' },
    close: { label: 'Close' },
  },
};

type OnboardingTipRow = {
  id: string;
  surface?: string;
  audience?: string;
  placement?: string;
  enabled?: boolean;
  title?: string;
  subtitle?: string;
  cta?: string;
  maxShows?: number;
};

type OnboardingTipsSettings = {
  enabled: boolean;
  version: number;
  tips: OnboardingTipRow[];
};

const DEFAULT_ONBOARDING_TIPS_SETTINGS: OnboardingTipsSettings = {
  enabled: false,
  version: 1,
  tips: [
    {
      id: 'feed.browse',
      surface: 'feed',
      audience: 'any',
      placement: 'inline.header',
      enabled: true,
      title: 'FEED',
      subtitle: 'Swipe for recaps and open challenges.',
      cta: 'Got it',
      maxShows: 1,
    },
    {
      id: 'play.map.discovery',
      surface: 'play',
      audience: 'any',
      placement: 'toast.map',
      enabled: true,
      title: 'PLAY',
      subtitle: 'Tap a pin to join. Long press anywhere to host.',
      cta: 'Got it',
      maxShows: 1,
    },
    {
      id: 'match.lobby.participant.join',
      surface: 'match_lobby',
      audience: 'participant',
      placement: 'inline.footer',
      enabled: true,
      title: 'JOIN',
      subtitle: 'Join to pick a side this round.',
      cta: 'Join game',
      maxShows: 1,
    },
    {
      id: 'match.lobby.controller.set_teams',
      surface: 'match_lobby',
      audience: 'controller',
      placement: 'inline.set_teams',
      enabled: true,
      title: 'SET TEAMS',
      subtitle: 'Add 1+ to Light and Dark to start.',
      cta: 'Got it',
      maxShows: 1,
    },
    {
      id: 'community.open_challenges',
      surface: 'community',
      audience: 'any',
      placement: 'inline.header',
      enabled: true,
      title: 'COMMUNITY',
      subtitle: "See who's open to play and jump into challenges.",
      cta: 'Got it',
      maxShows: 1,
    },
    {
      id: 'post_match.banter',
      surface: 'post_match',
      audience: 'any',
      placement: 'inline.modal',
      enabled: true,
      title: 'BANTER',
      subtitle: 'Drop a quick line—keeps the recap fun.',
      cta: 'Got it',
      maxShows: 1,
    },
  ],
};

type MatchCopyField = {
  step: string;
  audience: 'controller' | 'participants' | 'shared';
  path: string;
  label: string;
  help?: string;
  vars?: string[];
};

const MATCH_COPY_STEPS = ['lobby', 'game_on', 'round_complete', 'series_decision', 'loser_challenge', 'post_match'] as const;

const MATCH_COPY_FIELDS: MatchCopyField[] = [
  { step: 'global', audience: 'shared', path: 'stepper.heading', label: 'Stepper heading', help: 'Heading shown above the stepper.' },
  { step: 'global', audience: 'shared', path: 'sheets.pointsInfo.title', label: 'Points sheet title', help: 'Title shown at the top of Points & leaderboard sheet.' },
  { step: 'global', audience: 'shared', path: 'sheets.pointsInfo.bodyHtml', label: 'Points sheet content', help: 'Rich text (HTML). Supports {winPoints}.' },
  ...MATCH_COPY_STEPS.flatMap(
    (step): MatchCopyField[] => [
      { step, audience: 'shared', path: `stepper.labels.${step}`, label: 'Stepper label', help: 'Label shown in the stepper.' },
      { step, audience: 'shared', path: `controls.stepTitles.${step}`, label: 'Step title', help: 'Uppercase title shown in the controls panel.' },
    ]
  ),

  { step: 'lobby', audience: 'controller', path: 'content.lobby.hostGuidance.default', label: 'Controller guidance (default)' },
  { step: 'lobby', audience: 'controller', path: 'content.lobby.hostGuidance.sessionLocked', label: 'Controller guidance (teams locked)' },
  { step: 'lobby', audience: 'controller', path: 'content.lobby.hostGuidance.sessionLockedTeamsSet', label: 'Controller guidance (teams set)' },

  { step: 'lobby', audience: 'participants', path: 'content.lobby.participantGuidance.join', label: 'Participant guidance (join)' },
  { step: 'lobby', audience: 'participants', path: 'content.lobby.participantGuidance.joinSession', label: 'Participant guidance (join session)' },
  { step: 'lobby', audience: 'participants', path: 'content.lobby.participantGuidance.getNext', label: 'Participant guidance (get next)' },
  { step: 'lobby', audience: 'participants', path: 'content.lobby.participantGuidance.getNextSession', label: 'Participant guidance (get next session)' },
  { step: 'lobby', audience: 'participants', path: 'content.lobby.participantGuidance.alreadyJoining', label: 'Participant guidance (already joining)' },
  { step: 'lobby', audience: 'participants', path: 'content.lobby.participantGuidance.alreadyJoiningSession', label: 'Participant guidance (already joining session)' },
  { step: 'lobby', audience: 'participants', path: 'content.lobby.participantsTitle', label: 'Participants list title' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.activityTitle', label: 'Gym activity title' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.liftTitle', label: 'Gym lift title' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.optionalSubtitle', label: 'Gym activity subtitle (optional)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.liftSubtitle', label: 'Gym activity subtitle (lift challenge)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.selectedLockedSubtitle', label: 'Gym activity subtitle (selected locked)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.selectedLiftLockedSubtitle', label: 'Gym activity subtitle (lift locked)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.freeFlowLockedSubtitle', label: 'Gym activity subtitle (free flow locked)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.noLiftLockedSubtitle', label: 'Gym activity subtitle (no lift locked)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.freeFlowTitle', label: 'Gym free flow title' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.chooseLiftTitle', label: 'Gym empty lift title' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.selectedHintEditable', label: 'Gym activity hint (editable)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.selectedHintLocked', label: 'Gym activity hint (locked)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.emptyOptionalHint', label: 'Gym activity hint (empty optional)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.emptyLiftHint', label: 'Gym activity hint (empty lift)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.pickerTitle', label: 'Gym picker title' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.pickerLiftTitle', label: 'Gym picker title (lift)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.pickerSubtitle', label: 'Gym picker subtitle' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.pickerLiftSubtitle', label: 'Gym picker subtitle (lift)' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.freeFlowOptionTitle', label: 'Gym free flow option title' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.freeFlowOptionBody', label: 'Gym free flow option body' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.customActivityBody', label: 'Gym custom activity helper' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.activityOptionBody', label: 'Gym activity option helper' },
  { step: 'lobby', audience: 'shared', path: 'content.lobby.gymActivity.liftOptionBody', label: 'Gym lift option helper' },

  { step: 'lobby', audience: 'controller', path: 'controls.hostSection.waitingTitle', label: 'Host section title (waiting)' },
  { step: 'lobby', audience: 'controller', path: 'controls.hostSection.waitingSubtitle', label: 'Host section subtitle (waiting)' },
  { step: 'lobby', audience: 'controller', path: 'controls.hostSection.pickSidesSubtitle', label: 'Host section subtitle (pick sides)' },
  { step: 'lobby', audience: 'controller', path: 'controls.hostSection.readySubtitle', label: 'Host section subtitle (ready)' },

  { step: 'lobby', audience: 'participants', path: 'controls.entryCta.joinTitle', label: 'Entry CTA title (join)' },
  { step: 'lobby', audience: 'participants', path: 'controls.entryCta.getNextTitle', label: 'Entry CTA title (get next)' },
  { step: 'lobby', audience: 'participants', path: 'controls.entryCta.joinCta', label: 'Entry CTA button' },
  { step: 'lobby', audience: 'participants', path: 'controls.entryCta.joinHint', label: 'Entry CTA hint (join)' },
  { step: 'lobby', audience: 'participants', path: 'controls.entryCta.leaveHint', label: 'Entry CTA hint (leave)' },
  { step: 'lobby', audience: 'participants', path: 'controls.entryCta.hostPicksHint', label: 'Entry CTA hint (host picks)' },
  { step: 'lobby', audience: 'participants', path: 'controls.playerActions.subtitleLobbyWait', label: 'Player actions subtitle (lobby wait)', help: 'Supports {controller}.', vars: ['controller'] },

  { step: 'round_complete', audience: 'controller', path: 'controls.roundCompleteGate.title', label: 'Gate title' },
  { step: 'round_complete', audience: 'controller', path: 'controls.roundCompleteGate.subtitleHost', label: 'Gate subtitle (controller)' },
  { step: 'round_complete', audience: 'participants', path: 'controls.roundCompleteGate.subtitleWait', label: 'Gate subtitle (participants)', help: 'Supports {host}.', vars: ['host'] },
  { step: 'round_complete', audience: 'shared', path: 'controls.roundCompleteGate.addScoreCta', label: 'Gate CTA (add score)' },
  { step: 'round_complete', audience: 'shared', path: 'controls.roundCompleteGate.nextCta', label: 'Gate CTA (next)' },
  { step: 'round_complete', audience: 'shared', path: 'controls.roundCompleteGate.closeCta', label: 'Gate CTA (close)' },

  { step: 'round_complete', audience: 'controller', path: 'controls.resultButtons.title', label: 'Result buttons title' },
  { step: 'round_complete', audience: 'controller', path: 'controls.resultButtons.subtitle', label: 'Result buttons subtitle' },
  { step: 'round_complete', audience: 'controller', path: 'controls.resultButtons.tieLabel', label: 'Result buttons tie label' },
  { step: 'round_complete', audience: 'controller', path: 'controls.resultButtons.accessibilityHint', label: 'Result buttons accessibility hint' },

  { step: 'series_decision', audience: 'controller', path: 'controls.whatsNextRoundCompleteHost.title', label: "What's next title" },
  { step: 'series_decision', audience: 'controller', path: 'controls.whatsNextRoundCompleteHost.subtitle', label: "What's next subtitle" },

  { step: 'loser_challenge', audience: 'shared', path: 'controls.bonusDecision.title', label: 'Bonus decision title' },
  { step: 'loser_challenge', audience: 'controller', path: 'controls.bonusDecision.subtitleHost', label: 'Bonus decision subtitle (controller)' },
  { step: 'loser_challenge', audience: 'participants', path: 'controls.bonusDecision.subtitleWait', label: 'Bonus decision subtitle (participants)' },
  { step: 'loser_challenge', audience: 'shared', path: 'controls.bonusDecision.declineCta', label: 'Bonus decision CTA (decline)' },
  { step: 'loser_challenge', audience: 'shared', path: 'controls.bonusDecision.acceptCta', label: 'Bonus decision CTA (accept)' },
  { step: 'loser_challenge', audience: 'shared', path: 'controls.bonusDecision.closeCta', label: 'Bonus decision CTA (close)' },
  { step: 'loser_challenge', audience: 'controller', path: 'content.loserChallenge.modal.title', label: 'Loser challenge modal title' },
  { step: 'loser_challenge', audience: 'controller', path: 'content.loserChallenge.modal.body', label: 'Loser challenge modal body' },

  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.title', label: 'Recap footer title' },
  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.subtitle', label: 'Recap footer subtitle' },
  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.addPhotosCta', label: 'Recap footer CTA (add photos)' },
  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.addPhotosLoading', label: 'Recap footer CTA (uploading)' },
  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.shareCta', label: 'Recap footer CTA (share)' },

  { step: 'post_match', audience: 'participants', path: 'controls.wrapCloseOnly.title', label: 'Wrap close-only title' },
  { step: 'post_match', audience: 'participants', path: 'controls.wrapCloseOnly.subtitle', label: 'Wrap close-only subtitle' },
  { step: 'post_match', audience: 'participants', path: 'controls.wrapCloseOnly.closeCta', label: 'Wrap close-only CTA' },

  { step: 'global', audience: 'shared', path: 'controls.teamSelect.title', label: 'Team select title' },
  { step: 'global', audience: 'shared', path: 'controls.teamSelect.subtitle', label: 'Team select subtitle' },
  { step: 'global', audience: 'shared', path: 'controls.optionsSheet.title', label: 'Options sheet title' },
  { step: 'global', audience: 'shared', path: 'controls.optionsSheet.closeCta', label: 'Options sheet close CTA' },
  { step: 'global', audience: 'shared', path: 'controls.playerActions.title', label: 'Player actions title' },
  { step: 'global', audience: 'shared', path: 'controls.leaveTeam.label', label: 'Leave team label' },
];

const MATCH_COPY_ACTIONS: Array<[string, string]> = [
  ['start_match', 'Start match'],
  ['lock_teams', 'Lock teams'],
  ['invite_players', 'Invite players'],
  ['join_match', 'Join match'],
  ['leave_match', 'Leave match'],
  ['leave_team', 'Leave team'],
  ['cancel_match', 'Cancel match'],
  ['abandon_match', 'Abandon match'],
  ['submit_result_a', 'Submit result (A)'],
  ['submit_result_b', 'Submit result (B)'],
  ['submit_tie', 'Submit tie'],
  ['finish_match', 'Finish'],
  ['start_rematch', 'Rematch'],
  ['end_practice', 'Finish challenge'],
  ['get_directions', 'Directions'],
  ['show_qr', 'QR Join'],
  ['join_team', 'Join team'],
  ['close', 'Close'],
];

function resolveTab(search: URLSearchParams): TabKey {
  const raw = search.get('tab');
  if (raw === 'settings' || raw === 'reports' || raw === 'dashboard' || raw === 'users' || raw === 'cms' || raw === 'events') return raw;
  if (raw === 'match_copy') return 'match_copy';
  if (raw === 'location_copy') return 'location_copy';
  if (raw === 'play_mode_copy') return 'play_mode_copy';
  if (raw === 'sports') return 'sports';
  if (raw === 'sport_audit') return 'sport_audit';
  if (raw === 'onboarding') return 'onboarding';
  // Back-compat (older links): `tab=cms&cms=onboarding|match_copy|location_copy|play_mode_copy`.
  if (raw === 'cms') {
    const cms = search.get('cms');
    if (cms === 'onboarding') return 'onboarding';
    if (cms === 'location_copy') return 'location_copy';
    if (cms === 'play_mode_copy') return 'play_mode_copy';
    return 'match_copy';
  }
  return 'dashboard';
}

function normalizeAdminAccessRows(value: unknown): AdminAccessRow[] {
  const rows = Array.isArray(value)
    ? value
    : isObject(value) && Array.isArray((value as any).admins)
      ? (value as any).admins
      : [];
  return rows
    .map((row: unknown) => {
      if (!isObject(row) || typeof row.user_id !== 'string') return null;
      return {
        user_id: row.user_id,
        email: typeof row.email === 'string' ? row.email : null,
        username: typeof row.username === 'string' ? row.username : null,
        full_name: typeof row.full_name === 'string' ? row.full_name : null,
        created_at: typeof row.created_at === 'string' ? row.created_at : null,
      } satisfies AdminAccessRow;
    })
    .filter((row: AdminAccessRow | null): row is AdminAccessRow => row !== null);
}

function adminAccessLabel(row: AdminAccessRow) {
  return row.email || row.username || row.full_name || `${row.user_id.slice(0, 8)}…`;
}

function SportsTab({ session }: { session: Session }) {
  const [catalog, setCatalog] = useState<SportCatalogSettings>(() => emptySportCatalogSettings());
  const [loadedCatalog, setLoadedCatalog] = useState<SportCatalogSettings | null>(null);
  const [meta, setMeta] = useState<{ updated_at: string | null; updated_by: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<StatusMessage | null>(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<'all' | SportReleaseStage>('all');
  const [activeGroupId, setActiveGroupId] = useState<string>('trick-style');

  const hasChanges = loadedCatalog ? stableStringify(catalog) !== stableStringify(loadedCatalog) : false;
  const activeGroup = catalog.groups[activeGroupId] ?? catalog.groups['trick-style'];

  const groupedSports = useMemo(() => {
    const term = search.trim().toLowerCase();
    return SPORT_GROUPS_CATALOG
      .map((staticGroup) => {
        const group = catalog.groups[staticGroup.id];
        const sports = SPORT_IDS
          .map((sportId) => ({ sportId, sport: catalog.sports[sportId] }))
          .filter((entry) => Boolean(entry.sport))
          .filter((entry) => entry.sport.groupId === staticGroup.id)
          .filter((entry) => (stageFilter === 'all' ? true : entry.sport.stage === stageFilter))
          .filter((entry) => {
            if (!term) return true;
            const sport = entry.sport;
            return (
              entry.sportId.toLowerCase().includes(term) ||
              sport.label.toLowerCase().includes(term) ||
              sport.groupId.toLowerCase().includes(term) ||
              sport.internalNotes.toLowerCase().includes(term) ||
              sport.launchNotes.toLowerCase().includes(term)
            );
          })
          .sort((a, b) => a.sport.order - b.sport.order || a.sport.label.localeCompare(b.sport.label));
        return { staticGroup, group, sports };
      })
      .filter((entry) => entry.sports.length > 0 || !search.trim());
  }, [catalog, search, stageFilter]);

  const validationWarnings = useMemo(() => {
    const warnings: string[] = [];
    SPORT_GROUP_IDS.forEach((groupId) => {
      const group = catalog.groups[groupId];
      if (!group?.label.trim()) warnings.push(`${groupId} is missing a group label.`);
    });
    SPORT_IDS.forEach((sportId) => {
      const sport = catalog.sports[sportId];
      if (!sport?.label.trim()) warnings.push(`${sportId} is missing a sport label.`);
      if (!SPORT_GROUP_IDS.includes(sport?.groupId as any)) warnings.push(`${sportId} has an invalid group.`);
    });
    return warnings;
  }, [catalog]);

  const loadSports = useCallback(async () => {
    setLoading(true);
    setMsg({ tone: 'info', text: 'Loading sports catalog...' });
    try {
      const res = await adminFetch<{ value: SportCatalogSettings }>(session, { action: 'sports:get' });
      const next = normalizeSportCatalogSettings(res.value);
      setCatalog(next);
      setLoadedCatalog(next);
      setMeta({ updated_at: (res.settings as any)?.updated_at ?? null, updated_by: (res.settings as any)?.updated_by ?? null });
      setMsg({ tone: 'success', text: 'Loaded sports catalog.' });
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to load sports catalog.' });
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    void loadSports();
  }, [loadSports]);

  const saveSports = useCallback(async () => {
    if (validationWarnings.length) {
      setMsg({ tone: 'warning', text: validationWarnings[0] });
      return;
    }
    setLoading(true);
    setMsg({ tone: 'info', text: 'Saving sports catalog...' });
    try {
      const res = await adminFetch<{ value: SportCatalogSettings }>(session, { action: 'sports:save', value: catalog });
      const next = normalizeSportCatalogSettings(res.value);
      setCatalog(next);
      setLoadedCatalog(next);
      setMsg({ tone: 'success', text: 'Saved sports catalog.' });
      await loadSports();
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to save sports catalog.' });
    } finally {
      setLoading(false);
    }
  }, [catalog, loadSports, session, validationWarnings]);

  const updateGroup = useCallback((groupId: string, patch: Partial<SportCatalogGroupSettings>) => {
    setCatalog((prev) => ({
      ...prev,
      groups: { ...prev.groups, [groupId]: { ...prev.groups[groupId], ...patch } },
    }));
  }, []);

  const updateSport = useCallback((sportId: string, patch: Partial<SportCatalogSportSettings>) => {
    if (!sportId) return;
    setCatalog((prev) => ({
      ...prev,
      sports: { ...prev.sports, [sportId]: { ...prev.sports[sportId], ...patch } },
    }));
  }, []);

  const setGroupStage = useCallback((groupId: string, stage: SportReleaseStage) => {
    setCatalog((prev) => {
      const nextSports = { ...prev.sports };
      SPORT_IDS.forEach((sportId) => {
        if (nextSports[sportId]?.groupId === groupId) nextSports[sportId] = { ...nextSports[sportId], stage };
      });
      return {
        ...prev,
        groups: { ...prev.groups, [groupId]: { ...prev.groups[groupId], stage } },
        sports: nextSports,
      };
    });
  }, []);

  const duplicateTargetsToSports = useCallback((groupId: string) => {
    setCatalog((prev) => {
      const source = prev.groups[groupId]?.targeting ?? { users: [], percent: 0 };
      const nextSports = { ...prev.sports };
      SPORT_IDS.forEach((sportId) => {
        if (nextSports[sportId]?.groupId === groupId) {
          nextSports[sportId] = {
            ...nextSports[sportId],
            targeting: { users: [...source.users], percent: source.percent },
          };
        }
      });
      return { ...prev, sports: nextSports };
    });
  }, []);

  const resetDraft = useCallback(() => {
    const ok = window.confirm('Reset the draft to app defaults? This does not save until you press Save.');
    if (!ok) return;
    setCatalog(emptySportCatalogSettings());
    setMsg({ tone: 'warning', text: 'Draft reset to defaults. Save to publish it.' });
  }, []);

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">Sports</div>
          <div className="admin-subtitle">Release, presentation, and launch targeting for supported sports.</div>
          <div className="admin-muted">
            Stored in <span className="admin-mono">app_settings.sport_catalog_v1</span>.
            {meta ? <> Last updated {meta.updated_at ? formatDate(meta.updated_at) : '-'}{meta.updated_by ? ` by ${String(meta.updated_by).slice(0, 8)}...` : ''}.</> : null}
          </div>
        </div>
        <div className="admin-actions">
          <span className={clsx('admin-badge', hasChanges && 'override')}>{hasChanges ? 'Unsaved changes' : 'Saved'}</span>
          <button className="admin-btn" type="button" onClick={loadSports} disabled={loading}>Reload</button>
          <button className="admin-btn danger" type="button" onClick={resetDraft} disabled={loading}>Reset draft</button>
          <button className="admin-btn primary" type="button" onClick={saveSports} disabled={loading || validationWarnings.length > 0}>Save sports</button>
        </div>
      </div>

      {msg ? <div className={clsx('admin-status', msg.tone)}>{msg.text}</div> : null}
      {validationWarnings.length ? <div className="admin-status warning">{validationWarnings.slice(0, 3).join(' ')}</div> : null}

      <div className="admin-grid-2">
        <div className="admin-card">
          <div className="admin-section-title">Catalog controls</div>
          <div className="admin-grid-2" style={{ marginTop: 12 }}>
            <label className="admin-label">
              Search
              <input className="admin-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="HYROX, launch notes..." />
            </label>
            <label className="admin-label">
              Stage
              <select className="admin-select" value={stageFilter} onChange={(e) => setStageFilter(e.target.value as any)}>
                <option value="all">All stages</option>
                {SPORT_RELEASE_STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
              </select>
            </label>
          </div>
          <div className="admin-actions" style={{ marginTop: 12, justifyContent: 'flex-start' }}>
            {SPORT_RELEASE_STAGES.map((stage) => <span key={stage} className="admin-badge">{stage}: {countSportsByStage(catalog, stage)}</span>)}
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-section-title">Group editor</div>
          <div className="admin-stack" style={{ marginTop: 12 }}>
            <label className="admin-label">
              Group
              <select className="admin-select" value={activeGroupId} onChange={(e) => setActiveGroupId(e.target.value)}>
                {SPORT_GROUP_IDS.map((groupId) => <option key={groupId} value={groupId}>{catalog.groups[groupId]?.label ?? groupId}</option>)}
              </select>
            </label>
            {activeGroup ? (
              <>
                <div className="admin-grid-2">
                  <label className="admin-label">
                    Label
                    <input className="admin-input" value={activeGroup.label} onChange={(e) => updateGroup(activeGroupId, { label: e.target.value })} />
                  </label>
                  <label className="admin-label">
                    Stage
                    <select className="admin-select" value={activeGroup.stage} onChange={(e) => updateGroup(activeGroupId, { stage: e.target.value as SportReleaseStage })}>
                      {SPORT_RELEASE_STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                    </select>
                  </label>
                </div>
                <label className="admin-label">
                  Subtitle
                  <input className="admin-input" value={activeGroup.subtitle} onChange={(e) => updateGroup(activeGroupId, { subtitle: e.target.value })} />
                </label>
                <div className="admin-grid-2">
                  <label className="admin-label">
                    Target users/usernames
                    <textarea className="admin-textarea mono" rows={3} value={activeGroup.targeting.users.join('\n')} onChange={(e) => updateGroup(activeGroupId, { targeting: { ...activeGroup.targeting, users: parseIdentifierList(e.target.value) } })} />
                  </label>
                  <label className="admin-label">
                    Percent rollout
                    <input className="admin-input" inputMode="decimal" value={String(activeGroup.targeting.percent)} onChange={(e) => updateGroup(activeGroupId, { targeting: { ...activeGroup.targeting, percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } })} />
                  </label>
                </div>
                <div className="admin-actions" style={{ justifyContent: 'flex-start' }}>
                  <button className="admin-btn danger" type="button" onClick={() => setGroupStage(activeGroupId, 'hidden')}>Hide group</button>
                  <button className="admin-btn" type="button" onClick={() => setGroupStage(activeGroupId, 'internal')}>Internal only</button>
                  <button className="admin-btn" type="button" onClick={() => setGroupStage(activeGroupId, 'live')}>Set group live</button>
                  <button className="admin-btn" type="button" onClick={() => duplicateTargetsToSports(activeGroupId)}>Duplicate rollout targets</button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="admin-stack" style={{ marginTop: 16 }}>
        {groupedSports.map(({ staticGroup, group, sports }) => (
          <div className="admin-card" key={staticGroup.id}>
            <div className="admin-row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div className="admin-section-title">{group?.label ?? staticGroup.label}</div>
                <div className="admin-muted">{group?.subtitle ?? staticGroup.subtitle}</div>
              </div>
              <span className="admin-badge">{group?.stage ?? 'live'}</span>
            </div>

            <div className="admin-stack" style={{ marginTop: 12 }}>
              {sports.map(({ sportId, sport }) => (
                <details className="admin-details" key={sportId}>
                  <summary>
                    <span style={{ fontWeight: 800 }}>{sport.label}</span>
                    <span className="admin-badge" style={{ marginLeft: 10 }}>{sport.stage}</span>
                    {sport.featured ? <span className="admin-badge override" style={{ marginLeft: 8 }}>Featured</span> : null}
                  </summary>
                  <div className="admin-grid-2" style={{ marginTop: 12 }}>
                    <label className="admin-label">
                      Label
                      <input className="admin-input" value={sport.label} onChange={(e) => updateSport(sportId, { label: e.target.value })} />
                    </label>
                    <label className="admin-label">
                      Group
                      <select className="admin-select" value={sport.groupId} onChange={(e) => updateSport(sportId, { groupId: e.target.value })}>
                        {SPORT_GROUP_IDS.map((groupId) => <option key={groupId} value={groupId}>{catalog.groups[groupId]?.label ?? groupId}</option>)}
                      </select>
                    </label>
                    <label className="admin-label">
                      Stage
                      <select className="admin-select" value={sport.stage} onChange={(e) => updateSport(sportId, { stage: e.target.value as SportReleaseStage })}>
                        {SPORT_RELEASE_STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                      </select>
                    </label>
                    <label className="admin-label">
                      Order
                      <input className="admin-input" inputMode="numeric" value={String(sport.order)} onChange={(e) => updateSport(sportId, { order: Number(e.target.value) || 0 })} />
                    </label>
                    <label className="admin-check">
                      <input type="checkbox" checked={sport.featured} onChange={(e) => updateSport(sportId, { featured: e.target.checked })} />
                      Featured in launch planning
                    </label>
                    <label className="admin-label">
                      Percent rollout
                      <input className="admin-input" inputMode="decimal" value={String(sport.targeting.percent)} onChange={(e) => updateSport(sportId, { targeting: { ...sport.targeting, percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } })} />
                    </label>
                    <label className="admin-label">
                      Target users/usernames
                      <textarea className="admin-textarea mono" rows={3} value={sport.targeting.users.join('\n')} onChange={(e) => updateSport(sportId, { targeting: { ...sport.targeting, users: parseIdentifierList(e.target.value) } })} />
                    </label>
                    <label className="admin-label">
                      Internal notes
                      <textarea className="admin-textarea" rows={3} value={sport.internalNotes} onChange={(e) => updateSport(sportId, { internalNotes: e.target.value })} />
                    </label>
                    <label className="admin-label" style={{ gridColumn: '1 / -1' }}>
                      Launch notes
                      <textarea className="admin-textarea" rows={3} value={sport.launchNotes} onChange={(e) => updateSport(sportId, { launchNotes: e.target.value })} />
                    </label>
                  </div>
                </details>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ msg }: { msg: StatusMessage | null }) {
  if (!msg?.text) return null;
  return (
    <div className={clsx('admin-status', msg.tone)}>
      {msg.text}
    </div>
  );
}

function MiniSparkline({ series }: { series: number[] }) {
  const height = 40;
  const width = 180;
  const max = Math.max(1, ...series);
  const min = Math.min(0, ...series);
  const range = Math.max(1, max - min);
  const pts = series
    .map((v, i) => {
      const x = (i / Math.max(1, series.length - 1)) * (width - 2) + 1;
      const y = height - ((v - min) / range) * (height - 2) - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" className="admin-sparkline">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function formatNumber(value: number | null | undefined) {
  if (value === null || value === undefined) return '—';
  if (Number.isNaN(value)) return '—';
  return new Intl.NumberFormat().format(value);
}

function sumValues(values: number[]) {
  return values.reduce((acc, value) => acc + (Number.isFinite(value) ? value : 0), 0);
}

function computeTrendDelta(series: number[]) {
  if (!series || series.length < 14) {
    return { label: 'Need 14d', tone: 'info' as const };
  }
  const prev = sumValues(series.slice(0, 7));
  const last = sumValues(series.slice(7));
  if (prev === 0 && last === 0) return { label: 'No change', tone: 'info' as const };
  if (prev === 0 && last > 0) return { label: 'New', tone: 'success' as const };
  const delta = (last - prev) / Math.max(prev, 1);
  const deltaPct = Math.round(delta * 100);
  return { label: `${deltaPct >= 0 ? '+' : ''}${deltaPct}% vs prev`, tone: deltaPct >= 0 ? ('success' as const) : ('warning' as const) };
}

function TrendDeltaChip({ series }: { series: number[] }) {
  const delta = computeTrendDelta(series);
  return <span className={clsx('admin-chip', delta.tone)}>{delta.label}</span>;
}

function Funnel({ baseValue, rows }: { baseValue: number; rows: Array<{ label: string; value: number; meta?: string }> }) {
  if (!baseValue || baseValue <= 0) {
    return <div className="admin-muted">No funnel data yet.</div>;
  }
  return (
    <div className="admin-funnel">
      {rows.map((row) => {
        const percent = baseValue > 0 ? Math.round((row.value / baseValue) * 100) : 0;
        const width = Math.max(4, Math.min(100, percent));
        return (
          <div className="admin-funnel-step" key={row.label}>
            <div className="admin-funnel-row">
              <span>{row.label}</span>
              <span className="admin-mono">
                {formatNumber(row.value)}{percent ? ` · ${percent}%` : ''}
              </span>
            </div>
            <div className="admin-funnel-bar">
              <div className="admin-funnel-fill" style={{ width: `${width}%` }} />
            </div>
            {row.meta ? <div className="admin-muted">{row.meta}</div> : null}
          </div>
        );
      })}
      <div className="admin-muted" style={{ marginTop: 10 }}>Based on last 30 days.</div>
    </div>
  );
}

function InsightTabs({
  active,
  onChange,
}: {
  active: string;
  onChange: (key: string) => void;
}) {
  const items = [
    { key: 'sports', label: 'Top sports' },
    { key: 'venues', label: 'Top venues' },
    { key: 'recentVenues', label: 'Recent venues' },
    { key: 'areas', label: 'Popular areas' },
  ];
  return (
    <div className="admin-tabs" role="tablist" aria-label="Insights tabs">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className={clsx('admin-tab', active === item.key && 'active')}
          onClick={() => onChange(item.key)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function InsightList({
  rows,
  suffix,
}: {
  rows: Array<{ label: string; value: string; meta?: string }>;
  suffix?: string;
}) {
  if (!rows.length) return <div className="admin-muted">No data yet.</div>;
  return (
    <div className="admin-insight-list">
      {rows.map((row) => (
        <div className="admin-insight-row" key={`${row.label}-${row.value}`}>
          <div>
            <div className="admin-insight-label">{row.label}</div>
            {row.meta ? <div className="admin-muted">{row.meta}</div> : null}
          </div>
          <div className="admin-insight-value">{row.value}{suffix ? ` ${suffix}` : ''}</div>
        </div>
      ))}
    </div>
  );
}

function useAdminSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });
    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  return { session, loading };
}

function useIsAdmin(session: Session | null) {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!session?.user?.id) {
        if (mounted) setIsAdmin(null);
        return;
      }
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('app_admins')
          .select('user_id')
          .eq('user_id', session.user.id)
          .maybeSingle();
        if (!mounted) return;
        if (error && (error as any).code !== 'PGRST116') throw error;
        setIsAdmin(Boolean((data as any)?.user_id));
      } catch {
        if (mounted) setIsAdmin(false);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [session?.user?.id]);

  return { isAdmin, loading };
}

function AuthCard() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<StatusMessage | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSignIn = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ tone: 'info', text: 'Signing in…' });
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;
      setMsg({ tone: 'success', text: 'Signed in.' });
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Sign-in failed.' });
    } finally {
      setBusy(false);
    }
  }, [email, password]);

  const handleMagicLink = useCallback(async () => {
    setBusy(true);
    setMsg({ tone: 'info', text: 'Sending magic link…' });
    try {
      const redirectTo = STANDALONE_ADMIN
        ? `${window.location.origin}/`
        : `${window.location.origin}/app/admin/`;
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { emailRedirectTo: redirectTo },
      });
      if (error) throw error;
      setMsg({ tone: 'success', text: 'Magic link sent. Open it on this device.' });
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to send magic link.' });
    } finally {
      setBusy(false);
    }
  }, [email]);

  return (
    <div className="admin-shell">
      <main className="admin-auth">
        <div className="admin-card">
          <div className="admin-card-head">
            <div>
              <div className="admin-title">RVLRY Admin</div>
              <div className="admin-subtitle">Sign in to manage CMS + settings.</div>
            </div>
          </div>

          <StatusPill msg={msg} />

          <form onSubmit={handleSignIn} className="admin-form">
            <label className="admin-label">
              Email
              <input className="admin-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </label>
            <label className="admin-label">
              Password
              <input className="admin-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </label>
            <div className="admin-row">
              <button className="admin-btn primary" type="submit" disabled={busy || !email.trim() || !password}>
                Sign in
              </button>
              <button className="admin-btn" type="button" disabled={busy || !email.trim()} onClick={handleMagicLink}>
                Magic link
              </button>
            </div>
          </form>

          <div className="admin-muted">
            Supabase: <span className="admin-mono">{config.supabaseUrl}</span>
          </div>
        </div>
      </main>
    </div>
  );
}

function AdminLayout({
  session,
  activeTab,
  onTab,
  children,
}: {
  session: Session;
  activeTab: TabKey;
  onTab: (tab: TabKey) => void;
  children: React.ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const email = session.user.email ?? session.user.id;
  const projectRef = useMemo(() => {
    try {
      const host = new URL(config.supabaseUrl).host;
      return host.split('.')[0] ?? 'unknown';
    } catch {
      return 'unknown';
    }
  }, []);

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <div className="admin-topbar-row">
          <div className="admin-topbar-left">
            <button className="admin-topbar-menu" type="button" onClick={() => setMenuOpen((value) => !value)} aria-expanded={menuOpen} aria-label="Toggle navigation">
              Menu
            </button>
            <div className="admin-brand">
              <img src="/app/brand/rvlry-symbol.png" alt="RVLRY" />
              <span className="admin-brand-wordmark">Admin</span>
            </div>
            <nav className={clsx('admin-topbar-nav', menuOpen && 'open')} aria-label="Admin">
              {ADMIN_TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={clsx('admin-topbar-item', activeTab === tab.key && 'active')}
                  onClick={() => {
                    trackAdminEvent('admin_nav_click', {
                      action: 'click',
                      current_tab: activeTab,
                      target_tab: tab.key,
                    });
                    onTab(tab.key);
                    setMenuOpen(false);
                  }}
                  type="button"
                >
                  <span className="admin-nav-icon"><AdminTabIcon tab={tab.key} /></span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
          <div className="admin-topbar-right">
            <div className="admin-topbar-user">
              <div className="admin-user-email">{email}</div>
              <div className="admin-muted">Administrator · <span className="admin-mono">{projectRef}</span></div>
            </div>
            <button className="admin-btn danger" type="button" onClick={() => supabase.auth.signOut()} title="Log out" aria-label="Log out">
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main" onClick={() => setMenuOpen(false)}>
        {children}
      </main>
    </div>
  );
}

function MatchCopyCMS({ session }: { session: Session }) {
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState<{ updated_at: string | null; updated_by: string | null } | null>(null);

  const [step, setStep] = useState<'lobby' | 'game_on' | 'round_complete' | 'series_decision' | 'loser_challenge' | 'post_match' | 'global'>('lobby');
  const [audience, setAudience] = useState<'controller' | 'participants' | 'shared' | 'all'>('all');
  const [search, setSearch] = useState('');
  const [modifiedOnly, setModifiedOnly] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const [baseline, setBaseline] = useState<Record<string, unknown>>({});
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [needsNormalize, setNeedsNormalize] = useState(false);

  const dirty = useMemo(() => stableStringify(baseline) !== stableStringify(draft) || needsNormalize, [baseline, draft, needsNormalize]);

  const effective = useMemo(() => deepMerge(DEFAULT_MATCH_EXPERIENCE_COPY, draft) as Record<string, unknown>, [draft]);

  // Count overrides per step
  const overridesByStep = useMemo(() => {
    const counts: Record<string, number> = {};
    MATCH_COPY_FIELDS.forEach((f) => {
      const val = getPathValue(draft, f.path);
      if (typeof val === 'string' && val.trim().length > 0) {
        counts[f.step] = (counts[f.step] ?? 0) + 1;
      }
    });
    // Count action overrides as global
    MATCH_COPY_ACTIONS.forEach(([key]) => {
      const label = getPathValue(draft, `actions.${key}.label`);
      const loadingLabel = getPathValue(draft, `actions.${key}.loadingLabel`);
      if ((typeof label === 'string' && label.trim()) || (typeof loadingLabel === 'string' && loadingLabel.trim())) {
        counts['global'] = (counts['global'] ?? 0) + 1;
      }
    });
    return counts;
  }, [draft]);

  const totalOverrides = useMemo(() => Object.values(overridesByStep).reduce((a, b) => a + b, 0), [overridesByStep]);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus({ tone: 'info', text: 'Loading…' });
    try {
      const result = await adminFetch<{ value: any }>(session, { action: 'match_copy:get' });
      const stored = isObject(result.value) ? (result.value as Record<string, unknown>) : {};
      const merged = deepMerge(DEFAULT_MATCH_EXPERIENCE_COPY, stored);
      const minimized = (deepDiff(DEFAULT_MATCH_EXPERIENCE_COPY, merged) as Record<string, unknown> | undefined) ?? {};

      const normalizeNeeded = stableStringify(stored) !== stableStringify(minimized);
      setNeedsNormalize(normalizeNeeded);
      setBaseline(minimized);
      setDraft(JSON.parse(JSON.stringify(minimized)));
      setMeta({
        updated_at: (result.settings as any)?.updated_at ?? null,
        updated_by: (result.settings as any)?.updated_by ?? null,
      });
      const updatedAt = (result.settings as any)?.updated_at ? formatDate((result.settings as any)?.updated_at) : null;
      const updatedBy = (result.settings as any)?.updated_by ? String((result.settings as any)?.updated_by).slice(0, 8) + '…' : null;
      const suffix = normalizeNeeded ? ' Stored JSON includes defaults — click Save to normalize.' : '';
      setStatus({ tone: 'success', text: (updatedAt ? `Loaded. Last updated ${updatedAt}${updatedBy ? ` by ${updatedBy}` : ''}.` : 'Loaded (no row found).') + suffix });
    } catch (err) {
      setStatus({ tone: 'error', text: (err as any)?.message ?? 'Failed to load match copy.' });
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setLoading(true);
    setStatus({ tone: 'info', text: needsNormalize && stableStringify(baseline) === stableStringify(draft) ? 'Normalizing…' : 'Saving…' });
    try {
      await adminFetch(session, { action: 'match_copy:save', value: draft });
      setNeedsNormalize(false);
      await load();
      setStatus({ tone: 'success', text: 'Saved.' });
    } catch (err) {
      setStatus({ tone: 'error', text: (err as any)?.message ?? 'Failed to save.' });
    } finally {
      setLoading(false);
    }
  }, [session, draft, load, baseline, needsNormalize]);

  const reset = useCallback(() => {
    setNeedsNormalize(false);
    setDraft({});
    setExpandedFields(new Set());
    setStatus({ tone: 'success', text: 'Reset overrides (not saved yet).' });
  }, []);

  const toggleField = useCallback((path: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const setOverride = useCallback((path: string, text: string) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as Record<string, unknown>;
      const def = String(getPathValue(DEFAULT_MATCH_EXPERIENCE_COPY, path) ?? '');
      if (!text.trim().length || text === def) {
        deletePathValue(next, path);
      } else {
        setPathValue(next, path, text);
      }
      return next;
    });
  }, []);

  const resetField = useCallback((path: string) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as Record<string, unknown>;
      deletePathValue(next, path);
      return next;
    });
  }, []);

  // Filter fields
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return MATCH_COPY_FIELDS
      .filter((f) => f.step === step)
      .filter((f) => (audience === 'all' ? true : f.audience === audience))
      .filter((f) => (!q ? true : `${f.label} ${f.path}`.toLowerCase().includes(q)))
      .filter((f) => {
        if (!modifiedOnly) return true;
        const val = getPathValue(draft, f.path);
        return typeof val === 'string' && val.trim().length > 0;
      });
  }, [step, audience, search, modifiedOnly, draft]);

	  // Step configuration
	  const STEPS = [
	    { key: 'lobby', label: 'Lobby', num: 1 },
	    { key: 'game_on', label: 'Play On', num: 2 },
	    { key: 'round_complete', label: 'Round Over', num: 3 },
	    { key: 'series_decision', label: "What's Next", num: 4 },
	    { key: 'loser_challenge', label: 'Bonus Puzzle', num: 5 },
	    { key: 'post_match', label: 'Results', num: 6 },
	  ] as const;

	  const RichHtmlEditor = ({
	    value,
	    placeholder,
	    onChange,
	  }: {
	    value: string;
	    placeholder: string;
	    onChange: (next: string) => void;
	  }) => {
	    const ref = useRef<HTMLDivElement | null>(null);

	    useEffect(() => {
	      const el = ref.current;
	      if (!el) return;
	      const next = value || '';
	      if (el.innerHTML !== next) el.innerHTML = next;
	    }, [value]);

	    const exec = useCallback((cmd: string, arg?: string) => {
	      try {
	        (document as any).execCommand(cmd, false, arg);
	      } catch {
	        // ignore
	      }
	      ref.current?.focus();
	    }, []);

	    const addLink = useCallback(() => {
	      const url = window.prompt('Link URL');
	      if (!url) return;
	      exec('createLink', url);
	    }, [exec]);

	    return (
	      <div className="cms-rich" onClick={(e) => e.stopPropagation()}>
	        <div className="cms-rich-toolbar">
	          <button type="button" className="admin-btn small" onClick={() => exec('bold')}>
	            Bold
	          </button>
	          <button type="button" className="admin-btn small" onClick={() => exec('italic')}>
	            Italic
	          </button>
	          <button type="button" className="admin-btn small" onClick={() => exec('insertUnorderedList')}>
	            Bullets
	          </button>
	          <button type="button" className="admin-btn small" onClick={() => exec('formatBlock', 'h3')}>
	            H3
	          </button>
	          <button type="button" className="admin-btn small" onClick={() => exec('formatBlock', 'p')}>
	            P
	          </button>
	          <button type="button" className="admin-btn small" onClick={addLink}>
	            Link
	          </button>
	        </div>
	        <div
	          ref={ref}
	          className="cms-rich-editor"
	          contentEditable
	          suppressContentEditableWarning
	          data-placeholder={placeholder}
	          onInput={() => {
	            const html = ref.current?.innerHTML ?? '';
	            onChange(html);
	          }}
	        />
	        <div className="admin-help" style={{ marginTop: 8 }}>
	          Allowed: <span className="admin-mono">&lt;p&gt;</span>, <span className="admin-mono">&lt;h3&gt;</span>, <span className="admin-mono">&lt;ul&gt;</span>, <span className="admin-mono">&lt;li&gt;</span>, <span className="admin-mono">&lt;strong&gt;</span>, <span className="admin-mono">&lt;em&gt;</span>, <span className="admin-mono">&lt;hr&gt;</span>, <span className="admin-mono">&lt;small&gt;</span>.
	        </div>
	      </div>
	    );
	  };

	  // Compact Field Component
	  const Field = ({ field }: { field: MatchCopyField | { path: string; label: string; help?: string; vars?: string[] } }) => {
	    const def = getPathValue(DEFAULT_MATCH_EXPERIENCE_COPY, field.path);
	    const override = getPathValue(draft, field.path);
	    const current = getPathValue(effective, field.path);
    const defaultText = typeof def === 'string' ? def : '';
	    const overrideText = typeof override === 'string' ? override : '';
	    const currentText = typeof current === 'string' ? current : '';
	    const isRich = field.path === 'sheets.pointsInfo.bodyHtml';
	    const preview = isRich ? '(rich text)' : formatTemplate(currentText, { host: 'Alex' });
	    const hasOverride = typeof override === 'string' && override.trim().length > 0;
	    const isExpanded = expandedFields.has(field.path);

    return (
      <div className={clsx('cms-field', hasOverride && 'has-override', isExpanded && 'expanded')}>
        <div className="cms-field-header" onClick={() => toggleField(field.path)}>
          <div className="cms-field-info">
            <div className="cms-field-indicator" />
            <div className="cms-field-label">{field.label}</div>
            {!isExpanded && <div className="cms-field-preview">{preview || defaultText}</div>}
          </div>
          <span className="cms-field-expand">▼</span>
        </div>
	        <div className="cms-field-body">
	          {isRich ? (
	            <RichHtmlEditor value={overrideText} placeholder={defaultText} onChange={(next) => setOverride(field.path, next)} />
	          ) : (
	            <textarea
	              className="cms-field-input"
	              rows={2}
	              value={overrideText}
	              placeholder={defaultText}
	              spellCheck={false}
	              onChange={(e) => setOverride(field.path, e.target.value)}
	              onClick={(e) => e.stopPropagation()}
	            />
	          )}
	          <div className="cms-field-meta">
	            <span className="cms-field-meta-item">{field.path}</span>
	            {field.vars?.length ? <span className="cms-field-meta-item">vars: {field.vars.join(', ')}</span> : null}
	          </div>
          {field.help ? <div className="admin-help">{field.help}</div> : null}
          <div className="cms-field-default">
            <strong>Default:</strong> {defaultText || '(empty)'}
          </div>
          <div className="cms-field-actions">
            <button className="admin-btn small" type="button" disabled={!hasOverride} onClick={(e) => { e.stopPropagation(); resetField(field.path); }}>
              Reset to default
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Action Fields (for global step)
  const ActionFields = () => {
    const q = search.trim().toLowerCase();
    let items = MATCH_COPY_ACTIONS.filter(([k, label]) => (!q ? true : `${k} ${label}`.toLowerCase().includes(q)));

    if (modifiedOnly) {
      items = items.filter(([key]) => {
        const label = getPathValue(draft, `actions.${key}.label`);
        const loadingLabel = getPathValue(draft, `actions.${key}.loadingLabel`);
        return (typeof label === 'string' && label.trim()) || (typeof loadingLabel === 'string' && loadingLabel.trim());
      });
    }

    if (items.length === 0) return null;
    return (
      <div className="cms-section">
        <div className="cms-section-header">
          <div className="cms-section-title">Action buttons</div>
          <span className="cms-section-count">{items.length} actions</span>
        </div>
        <div className="cms-fields">
          {items.map(([key, label]) => (
            <div key={key} className="admin-grid-row">
              <Field field={{ path: `actions.${key}.label`, label: `${label} label` }} />
              <Field field={{ path: `actions.${key}.loadingLabel`, label: `${label} loading` }} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render fields by section
  const renderFields = () => {
    if (step === 'global') {
      return (
        <>
          {filtered.length > 0 && (
            <div className="cms-section">
              <div className="cms-section-header">
                <div className="cms-section-title">Global copy fields</div>
                <span className="cms-section-count">{filtered.length} fields</span>
              </div>
              <div className="cms-fields">
                {filtered.map((f) => <Field key={f.path} field={f} />)}
              </div>
            </div>
          )}
          <ActionFields />
        </>
      );
    }

    const controllerFields = filtered.filter((f) => f.audience === 'controller');
    const participantFields = filtered.filter((f) => f.audience === 'participants');
    const sharedFields = filtered.filter((f) => f.audience === 'shared');

    if (audience === 'all') {
      return (
        <>
          {controllerFields.length > 0 && (
            <div className="cms-section">
              <div className="cms-section-header">
                <div className="cms-section-title">Controller (Host) View</div>
                <span className="cms-section-count">{controllerFields.length} fields</span>
              </div>
              <div className="cms-fields">
                {controllerFields.map((f) => <Field key={f.path} field={f} />)}
              </div>
            </div>
          )}
          {participantFields.length > 0 && (
            <div className="cms-section">
              <div className="cms-section-header">
                <div className="cms-section-title">Participant View</div>
                <span className="cms-section-count">{participantFields.length} fields</span>
              </div>
              <div className="cms-fields">
                {participantFields.map((f) => <Field key={f.path} field={f} />)}
              </div>
            </div>
          )}
          {sharedFields.length > 0 && (
            <div className="cms-section">
              <div className="cms-section-header">
                <div className="cms-section-title">Shared (Both Views)</div>
                <span className="cms-section-count">{sharedFields.length} fields</span>
              </div>
              <div className="cms-fields">
                {sharedFields.map((f) => <Field key={f.path} field={f} />)}
              </div>
            </div>
          )}
        </>
      );
    }

    const title = audience === 'controller' ? 'Controller View' : audience === 'participants' ? 'Participant View' : 'Shared Fields';

    return (
      <div className="cms-section">
        <div className="cms-section-header">
          <div className="cms-section-title">{title}</div>
          <span className="cms-section-count">{filtered.length} fields</span>
        </div>
        <div className="cms-fields">
          {filtered.length ? filtered.map((f) => <Field key={f.path} field={f} />) : <div className="admin-muted">No fields match your filters.</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">Match Experience Copy</div>
          <div className="admin-subtitle">
            Customize text shown to users during matches
          </div>
        </div>
        <div className="cms-header-stats">
          {totalOverrides > 0 && (
            <span className="cms-override-count">{totalOverrides} override{totalOverrides !== 1 ? 's' : ''}</span>
          )}
          {dirty && (
            <span className={clsx('admin-badge', 'override')}>Unsaved</span>
          )}
          <button className="admin-btn" type="button" onClick={load} disabled={loading}>
            Reload
          </button>
          <button className="admin-btn" type="button" onClick={reset} disabled={loading}>
            Reset
          </button>
          <button className="admin-btn primary" type="button" onClick={save} disabled={loading || !dirty}>
            Save
          </button>
        </div>
      </div>

      <StatusPill msg={status} />

      {/* Visual Stepper */}
      <div className="cms-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <button
              type="button"
              className={clsx('cms-stepper-step', step === s.key && 'active', overridesByStep[s.key] && 'has-overrides')}
              onClick={() => setStep(s.key)}
            >
              <span className="cms-stepper-number">{s.num}</span>
              {s.label}
            </button>
            {i < STEPS.length - 1 && <span className="cms-stepper-arrow">→</span>}
          </React.Fragment>
        ))}
        <span className="cms-stepper-arrow">|</span>
        <button
          type="button"
          className={clsx('cms-stepper-step', step === 'global' && 'active', overridesByStep['global'] && 'has-overrides')}
          onClick={() => setStep('global')}
        >
          ⚙️ Global
        </button>
      </div>

      {/* Filters */}
      <div className="cms-filters">
        <span className="cms-filter-label">View:</span>
        <div className="admin-segmented">
          {(['all', 'controller', 'participants', 'shared'] as const).map((key) => (
            <button
              key={key}
              type="button"
              className={clsx('admin-seg', audience === key && 'active')}
              onClick={() => setAudience(key)}
            >
              {key === 'all' ? 'All' : key === 'controller' ? 'Host' : key === 'participants' ? 'Players' : 'Shared'}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={clsx('cms-filter-toggle', modifiedOnly && 'active')}
          onClick={() => setModifiedOnly((v) => !v)}
        >
          Modified only
        </button>
        <input
          className="admin-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search fields…"
          style={{ flex: 1, maxWidth: 240 }}
        />
      </div>

      {/* Fields */}
      <div>
        {renderFields()}
      </div>

      {/* Advanced */}
      <details className="admin-details">
        <summary>Advanced — Raw JSON</summary>
        <div className="admin-grid-2" style={{ marginTop: 12 }}>
          <div className="admin-card">
            <div className="admin-section-title">Overrides (saved)</div>
            <pre className="admin-code">{stableStringify(draft)}</pre>
          </div>
          <div className="admin-card">
            <div className="admin-section-title">Effective (merged)</div>
            <pre className="admin-code">{stableStringify(effective)}</pre>
          </div>
        </div>
        {meta && (
          <div className="admin-muted" style={{ marginTop: 10 }}>
            Last updated {meta.updated_at ? formatDate(meta.updated_at) : '—'}{meta.updated_by ? ` by ${String(meta.updated_by).slice(0, 8)}…` : ''}.
            Stored in <span className="admin-mono">app_settings.match_experience_copy_v1</span>.
          </div>
        )}
      </details>
    </div>
  );
}


function SettingsTab({ session }: { session: Session }) {
  const [msg, setMsg] = useState<StatusMessage | null>(null);
  const [loading, setLoading] = useState(false);

  const [feed, setFeed] = useState<FeedFilters>(DEFAULT_FEED_FILTERS);
  const [feedMeta, setFeedMeta] = useState<{ updated_at: string | null; updated_by: string | null } | null>(null);

  const [debug, setDebug] = useState<DebugFlags>(DEFAULT_DEBUG_FLAGS);
  const [pushGeofence, setPushGeofence] = useState<PushGeofenceSettings>(DEFAULT_PUSH_GEOFENCE);
  const [pushGeofenceMeta, setPushGeofenceMeta] = useState<{ updated_at: string | null; updated_by: string | null } | null>(null);
  const [flags, setFlags] = useState<FeatureFlagSettingsV2>(() => emptyFeatureFlagSettings());
  const [flagsMeta, setFlagsMeta] = useState<{ updated_at: string | null; updated_by: string | null } | null>(null);
  const [flagsGroup, setFlagsGroup] = useState('all');
  const [flagsSearch, setFlagsSearch] = useState('');

  const [pushTarget, setPushTarget] = useState('');
  const [pushTitle, setPushTitle] = useState('RVLRY');
  const [pushBody, setPushBody] = useState('Test push from admin.');
  const [pushData, setPushData] = useState('');
  const [pushResult, setPushResult] = useState('');
  const [adminAccessRows, setAdminAccessRows] = useState<AdminAccessRow[]>([]);
  const [adminAccessEmail, setAdminAccessEmail] = useState('');
  const [adminAccessLoading, setAdminAccessLoading] = useState(false);
  const [adminAccessMsg, setAdminAccessMsg] = useState<StatusMessage | null>(null);

  const envSelected = config.hubSelectedEnv as 'prod' | 'staging';
  const envActive = config.hubActiveEnv as 'prod' | 'staging';
  const envReady = envSelected === envActive;
  const [stagingDetailsOpen, setStagingDetailsOpen] = useState(() => envSelected === 'staging' && !envReady);

  const safeLsGet = useCallback((key: string) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }, []);

  const [stagingUrlDraft, setStagingUrlDraft] = useState(() => safeLsGet(config.hubLocalStorageKeys.stagingSupabaseUrl) ?? '');
  const [stagingAnonKeyDraft, setStagingAnonKeyDraft] = useState(() => safeLsGet(config.hubLocalStorageKeys.stagingSupabaseAnonKey) ?? '');

  const supabaseRef = useMemo(() => {
    try {
      return new URL(config.supabaseUrl).host.split('.')[0] ?? '';
    } catch {
      return '';
    }
  }, []);

  const switchEnv = useCallback((next: 'prod' | 'staging') => {
    try {
      window.localStorage.setItem(config.hubLocalStorageKeys.env, next);
    } catch {
      // ignore
    }
    const url = new URL(window.location.href);
    url.searchParams.set('env', next);
    window.location.href = url.toString();
  }, []);

  const saveStagingCreds = useCallback(() => {
    const urlRaw = stagingUrlDraft.trim();
    const keyRaw = stagingAnonKeyDraft.trim();
    let hostRef = '';
    try {
      hostRef = new URL(urlRaw).host.split('.')[0] ?? '';
    } catch {
      setMsg({ tone: 'error', text: 'Invalid staging Supabase URL.' });
      return;
    }

    const parts = keyRaw.split('.');
    if (parts.length < 2) {
      setMsg({ tone: 'error', text: 'Invalid staging anon key (not a JWT).' });
      return;
    }
    try {
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded));
      const jwtRef = typeof payload?.ref === 'string' ? payload.ref : '';
      if (jwtRef && hostRef && jwtRef !== hostRef) {
        setMsg({ tone: 'error', text: `Staging URL ref (${hostRef}) does not match anon key ref (${jwtRef}).` });
        return;
      }
    } catch {
      setMsg({ tone: 'error', text: 'Could not parse staging anon key JWT payload.' });
      return;
    }

    try {
      window.localStorage.setItem(config.hubLocalStorageKeys.stagingSupabaseUrl, urlRaw);
      window.localStorage.setItem(config.hubLocalStorageKeys.stagingSupabaseAnonKey, keyRaw);
      window.localStorage.setItem(config.hubLocalStorageKeys.env, 'staging');
    } catch {
      setMsg({ tone: 'error', text: 'Failed to save staging credentials to localStorage.' });
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('env', 'staging');
    window.location.href = url.toString();
  }, [stagingAnonKeyDraft, stagingUrlDraft]);

  const clearStagingCreds = useCallback(() => {
    const ok = window.confirm('Clear staging Supabase credentials from this browser?');
    if (!ok) return;
    try {
      window.localStorage.removeItem(config.hubLocalStorageKeys.stagingSupabaseUrl);
      window.localStorage.removeItem(config.hubLocalStorageKeys.stagingSupabaseAnonKey);
    } catch {
      // ignore
    }
    if (envSelected === 'staging') switchEnv('prod');
    else window.location.reload();
  }, [envSelected, switchEnv]);

  const loadAdminAccess = useCallback(async () => {
    if (!envReady) return;
    setAdminAccessLoading(true);
    try {
      const res = await adminFetch<{ value?: unknown }>(session, { action: 'admins:list' });
      const rows = normalizeAdminAccessRows(res.value);
      setAdminAccessRows(rows);
      setAdminAccessMsg({ tone: 'success', text: `Loaded ${rows.length} admin${rows.length === 1 ? '' : 's'}.` });
    } catch (err) {
      setAdminAccessMsg({ tone: 'warning', text: (err as any)?.message ?? 'Failed to load admin access.' });
    } finally {
      setAdminAccessLoading(false);
    }
  }, [envReady, session]);

  const loadAll = useCallback(async () => {
    if (!envReady) {
      setMsg({ tone: 'warning', text: 'Staging selected but not configured. Add staging Supabase URL + anon key below.' });
      return;
    }
    setLoading(true);
    setMsg({ tone: 'info', text: 'Loading…' });
    try {
      const [settings, debugRes, pushRes, flagsRes] = await Promise.all([
        adminFetch<{ value: FeedFilters }>(session, { action: 'settings:get' }),
        adminFetch<{ value: DebugFlags }>(session, { action: 'debug:get' }),
        adminFetch<{ value: PushGeofenceSettings }>(session, { action: 'push_geofence:get' }),
        adminFetch<{ value: FeatureFlagSettingsValue }>(session, { action: 'flags:get' }),
      ]);
      setFeed({ ...DEFAULT_FEED_FILTERS, ...(settings.value ?? {}) });
      setFeedMeta({ updated_at: (settings.settings as any)?.updated_at ?? null, updated_by: (settings.settings as any)?.updated_by ?? null });
      setDebug({ ...DEFAULT_DEBUG_FLAGS, ...(debugRes.value ?? {}) });
      setPushGeofence({ ...DEFAULT_PUSH_GEOFENCE, ...(pushRes.value ?? {}) });
      setPushGeofenceMeta({ updated_at: (pushRes.settings as any)?.updated_at ?? null, updated_by: (pushRes.settings as any)?.updated_by ?? null });
      setFlags(normalizeFeatureFlagSettings(flagsRes.value));
      setFlagsMeta({ updated_at: (flagsRes.settings as any)?.updated_at ?? null, updated_by: (flagsRes.settings as any)?.updated_by ?? null });
      await loadAdminAccess();
      setMsg({ tone: 'success', text: 'Loaded.' });
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to load.' });
    } finally {
      setLoading(false);
    }
  }, [envReady, loadAdminAccess, session]);

  useEffect(() => {
    if (envReady) loadAll();
    else setMsg({ tone: 'warning', text: 'Staging selected but not configured. Add staging Supabase URL + anon key below.' });
  }, [loadAll]);

  const saveFeed = useCallback(async () => {
    if (!envReady) {
      setMsg({ tone: 'warning', text: 'Staging selected but not configured. Add staging Supabase URL + anon key below.' });
      return;
    }
    setLoading(true);
    setMsg({ tone: 'info', text: 'Saving feed settings…' });
    try {
      await adminFetch(session, { action: 'settings:save', value: feed });
      setMsg({ tone: 'success', text: 'Saved feed settings.' });
      await loadAll();
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to save.' });
    } finally {
      setLoading(false);
    }
  }, [envReady, session, feed, loadAll]);

  const saveDebug = useCallback(async () => {
    if (!envReady) {
      setMsg({ tone: 'warning', text: 'Staging selected but not configured. Add staging Supabase URL + anon key below.' });
      return;
    }
    setLoading(true);
    setMsg({ tone: 'info', text: 'Saving debug settings…' });
    try {
      await adminFetch(session, { action: 'debug:save', value: debug });
      setMsg({ tone: 'success', text: 'Saved debug settings.' });
      await loadAll();
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to save.' });
    } finally {
      setLoading(false);
    }
  }, [envReady, session, debug, loadAll]);

  const savePushGeofence = useCallback(async () => {
    if (!envReady) {
      setMsg({ tone: 'warning', text: 'Staging selected but not configured. Add staging Supabase URL + anon key below.' });
      return;
    }
    setLoading(true);
    setMsg({ tone: 'info', text: 'Saving push settings…' });
    try {
      await adminFetch(session, { action: 'push_geofence:save', value: pushGeofence });
      setMsg({ tone: 'success', text: 'Saved push settings.' });
      await loadAll();
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to save.' });
    } finally {
      setLoading(false);
    }
  }, [envReady, session, pushGeofence, loadAll]);

  const saveFlagsValue = useCallback(async (nextFlags: FeatureFlagSettingsV2) => {
    if (!envReady) {
      setMsg({ tone: 'warning', text: 'Staging selected but not configured. Add staging Supabase URL + anon key below.' });
      return;
    }
    setLoading(true);
    setMsg({ tone: 'info', text: 'Saving feature flags…' });
    try {
      await adminFetch(session, { action: 'flags:save', value: nextFlags });
      setMsg({ tone: 'success', text: 'Saved feature flags.' });
      await loadAll();
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to save.' });
    } finally {
      setLoading(false);
    }
  }, [envReady, session, loadAll]);

  const saveFlags = useCallback(async () => {
    await saveFlagsValue(flags);
  }, [flags, saveFlagsValue]);

  const resetFlags = useCallback(async () => {
    const ok = window.confirm('Reset all feature flag overrides back to defaults?');
    if (!ok) return;
    const next = emptyFeatureFlagSettings();
    setFlags(next);
    await saveFlagsValue(next);
  }, [saveFlagsValue]);

  const flagsCatalog = useMemo(() => {
    return FEATURE_FLAG_KEYS.map((key) => ({
      key,
      group: groupForFlagKey(key),
      featured: (FEATURED_FLAG_KEYS as readonly string[]).includes(key),
      description: FEATURE_FLAG_DESCRIPTIONS[key],
    }));
  }, []);

  const groupOptions = useMemo(() => {
    const groups = Array.from(new Set(flagsCatalog.map((f) => f.group))).sort();
    return ['all', ...groups];
  }, [flagsCatalog]);

  const filteredFlags = useMemo(() => {
    const term = flagsSearch.trim().toLowerCase();
    return flagsCatalog
      .filter((f) => (flagsGroup === 'all' ? true : f.group === flagsGroup))
      .filter((f) => (!term ? true : (f.key.toLowerCase().includes(term) || f.description.toLowerCase().includes(term))))
      .sort((a, b) => {
        const aRank = a.featured ? -1 : 0;
        const bRank = b.featured ? -1 : 0;
        if (aRank !== bRank) return aRank - bRank;
        if (a.group !== b.group) return a.group.localeCompare(b.group);
        return a.key.localeCompare(b.key);
      });
  }, [flagsCatalog, flagsGroup, flagsSearch]);

  const sendPush = useCallback(async () => {
    if (!envReady) {
      setMsg({ tone: 'warning', text: 'Staging selected but not configured. Add staging Supabase URL + anon key below.' });
      return;
    }
    setLoading(true);
    setPushResult('');
    setMsg({ tone: 'info', text: 'Sending push…' });
    try {
      let data: Record<string, unknown> | undefined = undefined;
      if (pushData.trim().length) {
        data = JSON.parse(pushData);
        if (!isObject(data)) throw new Error('Data JSON must be an object.');
      }
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pushTarget.trim());
      const payload: Record<string, unknown> = { action: 'push:test', title: pushTitle, body: pushBody };
      if (isUuid) payload.targetUserId = pushTarget.trim();
      else payload.username = pushTarget.trim().replace(/^@/, '');
      if (data) payload.data = data;
      const res = await adminFetch<any>(session, payload);
      setPushResult(JSON.stringify(res, null, 2));
      setMsg({ tone: 'success', text: 'Push sent.' });
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to send push.' });
    } finally {
      setLoading(false);
    }
  }, [envReady, session, pushTarget, pushTitle, pushBody, pushData]);

  const addAdminAccess = useCallback(async () => {
    if (!envReady) {
      setAdminAccessMsg({ tone: 'warning', text: 'Staging selected but not configured.' });
      return;
    }
    const email = adminAccessEmail.trim().toLowerCase();
    if (!email) {
      setAdminAccessMsg({ tone: 'warning', text: 'Enter an existing user email.' });
      return;
    }
    setAdminAccessLoading(true);
    setAdminAccessMsg({ tone: 'info', text: `Granting access to ${email}…` });
    try {
      await adminFetch(session, { action: 'admins:add', email });
      setAdminAccessEmail('');
      await loadAdminAccess();
      setAdminAccessMsg({ tone: 'success', text: `Granted admin access to ${email}.` });
    } catch (err) {
      setAdminAccessMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to grant admin access.' });
    } finally {
      setAdminAccessLoading(false);
    }
  }, [adminAccessEmail, envReady, loadAdminAccess, session]);

  const removeAdminAccess = useCallback(async (row: AdminAccessRow) => {
    if (!envReady) {
      setAdminAccessMsg({ tone: 'warning', text: 'Staging selected but not configured.' });
      return;
    }
    const label = adminAccessLabel(row);
    const ok = window.confirm(`Remove admin access for ${label}?`);
    if (!ok) return;
    if (!row.user_id && !row.email) {
      setAdminAccessMsg({ tone: 'error', text: 'This admin row is missing an identifier, so it cannot be removed from the browser yet.' });
      return;
    }
    setAdminAccessLoading(true);
    setAdminAccessMsg({ tone: 'info', text: `Removing access for ${label}…` });
    try {
      await adminFetch(session, { action: 'admins:remove', email: row.email, user_id: row.user_id });
      await loadAdminAccess();
      setAdminAccessMsg({ tone: 'success', text: `Removed admin access for ${label}.` });
    } catch (err) {
      setAdminAccessMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to remove admin access.' });
    } finally {
      setAdminAccessLoading(false);
    }
  }, [envReady, loadAdminAccess, session]);

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">Settings</div>
          <div className="admin-subtitle">Global configuration & admin tools.</div>
          {feedMeta ? (
            <div className="admin-muted">
              Feed last updated {feedMeta.updated_at ? formatDate(feedMeta.updated_at) : '—'}{feedMeta.updated_by ? ` by ${String(feedMeta.updated_by).slice(0, 8)}…` : ''}.
            </div>
          ) : null}
        </div>
        <div className="admin-actions">
          <label className="admin-label" style={{ width: 200 }}>
            Environment
            <select
              className="admin-select"
              value={envSelected}
              onChange={(e) => switchEnv(e.target.value === 'staging' ? 'staging' : 'prod')}
              disabled={loading}
            >
              <option value="prod">Production</option>
              <option value="staging">Staging</option>
            </select>
          </label>
          <div className="admin-muted" style={{ alignSelf: 'flex-end' }}>
            Project <span className="admin-mono">{supabaseRef || '—'}</span>
            {envSelected !== envActive ? <span className="admin-badge" style={{ marginLeft: 8 }}>Needs setup</span> : null}
          </div>
          <button className="admin-btn" type="button" onClick={loadAll} disabled={loading || !envReady}>
            Reload
          </button>
        </div>
      </div>

      <StatusPill msg={msg} />

      {envSelected === 'staging' ? (
        <details
          className="admin-details"
          open={stagingDetailsOpen}
          onToggle={(e) => setStagingDetailsOpen((e.currentTarget as HTMLDetailsElement).open)}
          style={{ marginTop: 12 }}
        >
          <summary>Staging connection</summary>
          <div className="admin-muted" style={{ marginTop: 10 }}>
            Staging credentials are stored only in this browser (localStorage). They are required to edit staging settings.
          </div>
          <div className="admin-grid-2" style={{ marginTop: 12 }}>
            <label className="admin-label">
              Staging Supabase URL
              <input
                className="admin-input"
                value={stagingUrlDraft}
                onChange={(e) => setStagingUrlDraft(e.target.value)}
                placeholder="https://<ref>.supabase.co"
              />
            </label>
            <label className="admin-label">
              Staging anon key
              <input
                className="admin-input"
                value={stagingAnonKeyDraft}
                onChange={(e) => setStagingAnonKeyDraft(e.target.value)}
                placeholder="eyJhbGciOi..."
              />
            </label>
          </div>
          <div className="admin-actions" style={{ marginTop: 12, justifyContent: 'flex-start' }}>
            <button className="admin-btn primary" type="button" onClick={saveStagingCreds} disabled={loading || !stagingUrlDraft.trim() || !stagingAnonKeyDraft.trim()}>
              Save & switch to staging
            </button>
            <button className="admin-btn" type="button" onClick={clearStagingCreds} disabled={loading}>
              Clear staging creds
            </button>
          </div>
        </details>
      ) : null}

      <div className="admin-grid-2">
        <div className="admin-card">
          <div className="admin-section-title">Discovery & Feed</div>
          <div className="admin-stack">
            <label className="admin-label">
              Discovery radius (km)
              <input className="admin-input" inputMode="decimal" value={String(feed.nearbyRadiusKm)} onChange={(e) => setFeed((p) => ({ ...p, nearbyRadiusKm: Number(e.target.value) }))} />
            </label>
            <label className="admin-label">
              Recent days to inject
              <input className="admin-input" inputMode="numeric" value={String(feed.injectRecentDays)} onChange={(e) => setFeed((p) => ({ ...p, injectRecentDays: Number(e.target.value) }))} />
            </label>
            <label className="admin-label">
              Max injected items
              <input className="admin-input" inputMode="numeric" value={String(feed.injectMaxItems)} onChange={(e) => setFeed((p) => ({ ...p, injectMaxItems: Number(e.target.value) }))} />
            </label>

            <label className="admin-check">
              <input type="checkbox" checked={feed.includeNearbyVenues} onChange={() => setFeed((p) => ({ ...p, includeNearbyVenues: !p.includeNearbyVenues }))} />
              Include nearby venues
            </label>
            <label className="admin-check">
              <input type="checkbox" checked={feed.includeFollowedVenues} onChange={() => setFeed((p) => ({ ...p, includeFollowedVenues: !p.includeFollowedVenues }))} />
              Include followed venues
            </label>
            <label className="admin-check">
              <input type="checkbox" checked={feed.includeFollowedUsers} onChange={() => setFeed((p) => ({ ...p, includeFollowedUsers: !p.includeFollowedUsers }))} />
              Include followed users
            </label>
            <label className="admin-check">
              <input type="checkbox" checked={feed.includeViewerParticipation} onChange={() => setFeed((p) => ({ ...p, includeViewerParticipation: !p.includeViewerParticipation }))} />
              Always include viewer participation
            </label>
            <button className="admin-btn primary" type="button" onClick={saveFeed} disabled={loading || !envReady}>
              Save feed settings
            </button>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-section-title">Push notifications</div>
          <div className="admin-muted" style={{ marginTop: 8 }}>
            Geofence controls apply to <span className="admin-mono">match_created</span> pushes when the user has enabled Nearby only in the app.
            {pushGeofenceMeta ? (
              <> Last updated {pushGeofenceMeta.updated_at ? formatDate(pushGeofenceMeta.updated_at) : '—'}{pushGeofenceMeta.updated_by ? ` by ${String(pushGeofenceMeta.updated_by).slice(0, 8)}…` : ''}.</>
            ) : null}
          </div>
          <div className="admin-stack" style={{ marginTop: 12 }}>
            <label className="admin-check">
              <input
                type="checkbox"
                checked={pushGeofence.matchCreatedEnabled}
                onChange={() => setPushGeofence((p) => ({ ...p, matchCreatedEnabled: !p.matchCreatedEnabled }))}
              />
              Enable match created geofence
            </label>
            <label className="admin-label">
              Radius (km)
              <input
                className="admin-input"
                inputMode="decimal"
                value={String(pushGeofence.matchCreatedRadiusKm)}
                onChange={(e) => setPushGeofence((p) => ({ ...p, matchCreatedRadiusKm: Number(e.target.value) }))}
              />
            </label>
            <label className="admin-label">
              Max location age (hours)
              <input
                className="admin-input"
                inputMode="numeric"
                value={String(pushGeofence.matchCreatedMaxAgeHours)}
                onChange={(e) => setPushGeofence((p) => ({ ...p, matchCreatedMaxAgeHours: Number(e.target.value) }))}
              />
            </label>
            <button className="admin-btn primary" type="button" onClick={savePushGeofence} disabled={loading || !envReady}>
              Save push settings
            </button>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-section-title">Admin access</div>
          <div className="admin-muted" style={{ marginTop: 8 }}>
            Grant or remove app admin access by existing user email. This controls access to the admin shell and admin-only preview tools.
          </div>
          <div className="admin-grid-2" style={{ marginTop: 12 }}>
            <label className="admin-label" style={{ gridColumn: '1 / -1' }}>
              User email
              <input
                className="admin-input"
                value={adminAccessEmail}
                onChange={(e) => setAdminAccessEmail(e.target.value)}
                placeholder="rudy@rudynunes.com"
                autoComplete="email"
              />
            </label>
          </div>
          <div className="admin-actions" style={{ marginTop: 12, justifyContent: 'flex-start' }}>
            <button className="admin-btn primary" type="button" onClick={addAdminAccess} disabled={adminAccessLoading || !adminAccessEmail.trim() || !envReady}>
              Grant access
            </button>
            <button className="admin-btn" type="button" onClick={loadAdminAccess} disabled={adminAccessLoading || !envReady}>
              Reload
            </button>
          </div>
          {adminAccessMsg ? (
            <div className={clsx('admin-status', adminAccessMsg.tone)} style={{ marginTop: 12 }}>
              {adminAccessMsg.text}
            </div>
          ) : null}
          <div className="admin-stack" style={{ marginTop: 12 }}>
            {adminAccessRows.length === 0 ? (
              <div className="admin-muted">No admin rows loaded yet.</div>
            ) : (
              adminAccessRows.map((row) => {
                const isSelf = row.user_id === session.user.id;
                const isLastAdmin = adminAccessRows.length <= 1;
                const canRemove = !adminAccessLoading && !isSelf && !isLastAdmin;
                const removeTitle = isSelf
                  ? 'You cannot remove your own admin access here.'
                  : isLastAdmin
                    ? 'Keep at least one admin with access.'
                    : undefined;
                return (
                  <div
                    key={row.user_id}
                    className="admin-row"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: '1px solid var(--admin-border, rgba(148,163,184,0.22))',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {adminAccessLabel(row)}
                      </div>
                      <div className="admin-muted">
                        <span className="admin-mono">{row.user_id}</span>
                        {row.created_at ? ` · Added ${formatDate(row.created_at)}` : ''}
                      </div>
                    </div>
                    <button
                      className="admin-btn danger"
                      type="button"
                      onClick={() => removeAdminAccess(row)}
                      disabled={!canRemove}
                      title={removeTitle}
                    >
                      Remove
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-section-title">Debug</div>
          <div className="admin-stack">
            <label className="admin-check">
              <input type="checkbox" checked={debug.lobbyFeatureFlags} onChange={() => setDebug((p) => ({ ...p, lobbyFeatureFlags: !p.lobbyFeatureFlags }))} />
              Show lobby debug panel for users
            </label>
            <button className="admin-btn primary" type="button" onClick={saveDebug} disabled={loading || !envReady}>
              Save debug settings
            </button>
          </div>

          <div className="admin-section-title" style={{ marginTop: 18 }}>
            Feature flags
          </div>
          <div className="admin-muted" style={{ marginTop: 8 }}>
            Defaults apply globally; rollout overrides can target specific users or a percent of users. <span className="admin-badge">Default</span> means no global override (app code decides).
            {flagsMeta ? (
              <> Last updated {flagsMeta.updated_at ? formatDate(flagsMeta.updated_at) : '—'}{flagsMeta.updated_by ? ` by ${String(flagsMeta.updated_by).slice(0, 8)}…` : ''}.</>
            ) : null}
          </div>

          <div className="admin-flags-toolbar" style={{ marginTop: 12 }}>
            <label className="admin-label" style={{ flex: '1 1 260px' }}>
              Search
              <input className="admin-input" value={flagsSearch} onChange={(e) => setFlagsSearch(e.target.value)} placeholder="SESSION_V1…" />
            </label>
            <label className="admin-label" style={{ width: 220 }}>
              Group
              <select className="admin-select" value={flagsGroup} onChange={(e) => setFlagsGroup(e.target.value)}>
                {groupOptions.map((g) => (
                  <option key={g} value={g}>
                    {g === 'all' ? 'All groups' : g}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="admin-actions" style={{ marginTop: 10, justifyContent: 'flex-start' }}>
            <span className="admin-badge">{countFlagOverrides(flags)} overrides</span>
            <button className="admin-btn" type="button" onClick={() => { setFlagsSearch(''); setFlagsGroup('all'); }} disabled={loading}>
              Clear filters
            </button>
            <button className="admin-btn danger" type="button" onClick={resetFlags} disabled={loading || !envReady}>
              Reset overrides
            </button>
            <button className="admin-btn primary" type="button" onClick={saveFlags} disabled={loading || !envReady}>
              Save feature flags
            </button>
          </div>

          <div className="admin-flags-list" style={{ marginTop: 12 }}>
            {filteredFlags.map((flag) => {
              const state = getFlagTriState(flags, flag.key);
              const usersOn = flags.usersOn[flag.key] ?? [];
              const usersOff = flags.usersOff[flag.key] ?? [];
              const rollout = flags.rollouts[flag.key] ?? null;
              const rolloutMode: FeatureFlagRolloutMode = rollout?.mode ?? 'on';
              const rolloutPercent = rollout?.percent ?? 0;
              return (
                <div className={clsx('admin-flag-row', flag.featured && 'featured')} key={flag.key}>
                  <div className="admin-flag-top">
                    <div className="admin-flag-main">
                      <div className="admin-flag-key">
                        <span className="admin-mono">{flag.key}</span>
                        <span className="admin-badge" style={{ marginLeft: 10 }}>{flag.group}</span>
                        {flag.featured ? <span className="admin-badge override" style={{ marginLeft: 8 }}>Recommended</span> : null}
                      </div>
                      <div className="admin-flag-desc">{flag.description}</div>
                      <div className="admin-flag-meta">
                        {state === 'default' ? (
                          <span className="admin-badge">Default</span>
                        ) : (
                          <span className="admin-badge override">Override: {state === 'on' ? 'On' : 'Off'}</span>
                        )}
                        {rolloutPercent > 0 ? <span className="admin-badge override">Rollout: {rolloutMode} {rolloutPercent}%</span> : null}
                        {usersOn.length ? <span className="admin-badge override">Users on: {usersOn.length}</span> : null}
                        {usersOff.length ? <span className="admin-badge override">Users off: {usersOff.length}</span> : null}
                      </div>
                    </div>

                    <div className="admin-segmented" aria-label={`Feature flag ${flag.key}`}>
                      <button
                        className={clsx('admin-seg', state === 'default' && 'active')}
                        type="button"
                        onClick={() => setFlags((p) => applyFlagTriState(p, flag.key, 'default'))}
                        disabled={loading}
                      >
                        Default
                      </button>
                      <button
                        className={clsx('admin-seg', state === 'on' && 'active')}
                        type="button"
                        onClick={() => setFlags((p) => applyFlagTriState(p, flag.key, 'on'))}
                        disabled={loading}
                      >
                        On
                      </button>
                      <button
                        className={clsx('admin-seg', state === 'off' && 'active')}
                        type="button"
                        onClick={() => setFlags((p) => applyFlagTriState(p, flag.key, 'off'))}
                        disabled={loading}
                      >
                        Off
                      </button>
                    </div>
                  </div>

                  <details className="admin-flag-rollout">
                    <summary>
                      Rollout (users + percent)
                      {usersOn.length || usersOff.length || rolloutPercent > 0
                        ? ` — on: ${usersOn.length}, off: ${usersOff.length}, ${rolloutMode} ${rolloutPercent}%`
                        : ''}
                    </summary>

                    <div className="admin-grid-2" style={{ marginTop: 12 }}>
                      <label className="admin-label">
                        Enable for (user ids or @username)
                        <textarea
                          className="admin-textarea mono"
                          rows={3}
                          value={usersOn.join('\n')}
                          onChange={(e) => setFlags((p) => applyFlagUserTargets(p, flag.key, 'usersOn', parseIdentifierList(e.target.value)))}
                          placeholder="uuid-1\n@rudy\nusername"
                          disabled={loading}
                        />
                      </label>
                      <label className="admin-label">
                        Disable for (user ids or @username)
                        <textarea
                          className="admin-textarea mono"
                          rows={3}
                          value={usersOff.join('\n')}
                          onChange={(e) => setFlags((p) => applyFlagUserTargets(p, flag.key, 'usersOff', parseIdentifierList(e.target.value)))}
                          placeholder="uuid-2\n@somebody"
                          disabled={loading}
                        />
                      </label>
                    </div>

                    <div className="admin-grid-2" style={{ marginTop: 12 }}>
                      <label className="admin-label">
                        Percent rollout mode
                        <select
                          className="admin-select"
                          value={rolloutMode}
                          onChange={(e) => setFlags((p) => applyFlagRollout(p, flag.key, { mode: (e.target.value === 'off' ? 'off' : 'on'), percent: rolloutPercent }))}
                          disabled={loading}
                        >
                          <option value="on">Force ON</option>
                          <option value="off">Force OFF</option>
                        </select>
                      </label>
                      <label className="admin-label">
                        Percent rollout (0–100)
                        <input
                          className="admin-input"
                          inputMode="decimal"
                          value={String(rolloutPercent)}
                          onChange={(e) => {
                            const num = Number(e.target.value);
                            const pct = Number.isFinite(num) ? Math.max(0, Math.min(100, num)) : 0;
                            setFlags((p) => applyFlagRollout(p, flag.key, pct > 0 ? { mode: rolloutMode, percent: Math.round(pct * 100) / 100 } : null));
                          }}
                          placeholder="0"
                          disabled={loading}
                        />
                      </label>
                    </div>

                    <div className="admin-muted" style={{ marginTop: 8 }}>
                      Targets accept UUIDs or usernames (with/without <span className="admin-mono">@</span>). User targets win over percent rollout, which wins over the global Default/On/Off setting.
                    </div>
                  </details>
                </div>
              );
            })}
          </div>

          <details className="admin-details">
            <summary>Raw JSON (advanced)</summary>
            <div className="admin-stack" style={{ marginTop: 12 }}>
              <textarea
                className="admin-textarea mono"
                rows={10}
                value={stableStringify(flags)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    if (!isObject(parsed)) return;
                    setFlags(normalizeFeatureFlagSettings(parsed));
                  } catch {
                    // keep last good
                  }
                }}
              />
              <div className="admin-muted">
                Only known flag keys are saved; unknown keys are ignored server-side.
              </div>
            </div>
          </details>
        </div>
      </div>

      <div className="admin-card" style={{ marginTop: 16 }}>
        <div className="admin-section-title">Push notifications</div>
        <div className="admin-grid-2" style={{ marginTop: 12 }}>
          <label className="admin-label">
            Target (username or user id)
            <input className="admin-input" value={pushTarget} onChange={(e) => setPushTarget(e.target.value)} placeholder="@rudy or uuid" />
          </label>
          <label className="admin-label">
            Title
            <input className="admin-input" value={pushTitle} onChange={(e) => setPushTitle(e.target.value)} />
          </label>
          <label className="admin-label" style={{ gridColumn: '1 / -1' }}>
            Body
            <input className="admin-input" value={pushBody} onChange={(e) => setPushBody(e.target.value)} />
          </label>
          <label className="admin-label" style={{ gridColumn: '1 / -1' }}>
            Data JSON (optional)
            <textarea className="admin-textarea mono" rows={4} value={pushData} onChange={(e) => setPushData(e.target.value)} placeholder='{"deepLink":"rvlry://community"}' />
          </label>
        </div>
        <div className="admin-actions" style={{ marginTop: 12 }}>
          <button className="admin-btn primary" type="button" onClick={sendPush} disabled={loading || !envReady || !pushTarget.trim()}>
            Send test push
          </button>
          <button className="admin-btn" type="button" onClick={() => { setPushData(''); setPushResult(''); }} disabled={loading}>
            Clear
          </button>
        </div>
        {pushResult ? (
          <pre className="admin-code" style={{ marginTop: 12 }}>{pushResult}</pre>
        ) : null}
      </div>
    </div>
  );
}

function ReportsTab({ session }: { session: Session }) {
  const [clientStatus, setClientStatus] = useState<'all' | ClientErrorStatus>('open');
  const [clientTerm, setClientTerm] = useState('');
  const [clientMsg, setClientMsg] = useState<StatusMessage | null>(null);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientRows, setClientRows] = useState<ClientErrorRow[]>([]);

  const loadClientErrors = useCallback(async () => {
    setClientLoading(true);
    setClientMsg({ tone: 'info', text: 'Loading notification errors…' });
    try {
      let query = supabase
        .from('client_error_events')
        .select(
          'id, created_at, status, event_name, user_id, match_id, route, notification_type, error_message, error_code, error_stack, app_version, platform, notification_payload, params, build'
        )
        .eq('event_name', 'push_open_match_error')
        .order('created_at', { ascending: false })
        .limit(80);

      if (clientStatus !== 'all') {
        query = query.eq('status', clientStatus);
      }
      const q = clientTerm.trim();
      if (q) {
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(q);
        if (isUuid) {
          query = query.or(`id.eq.${q},match_id.eq.${q},user_id.eq.${q}`);
        } else {
          query = query.or(`event_name.ilike.%${q}%,route.ilike.%${q}%`);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      setClientRows((data ?? []) as ClientErrorRow[]);
      setClientMsg({ tone: 'success', text: `Loaded ${data?.length ?? 0} notification errors.` });
    } catch (err) {
      setClientMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to load notification errors.' });
    } finally {
      setClientLoading(false);
    }
  }, [clientStatus, clientTerm]);

  useEffect(() => {
    loadClientErrors();
  }, [loadClientErrors]);

  const updateClientError = useCallback(async (id: string, next: ClientErrorStatus) => {
    setClientLoading(true);
    try {
      const { error } = await supabase.from('client_error_events').update({ status: next }).eq('id', id);
      if (error) throw error;
      setClientMsg({ tone: 'success', text: `Updated ${id.slice(0, 8)}… to ${next}.` });
      await loadClientErrors();
    } catch (err) {
      setClientMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to update notification error.' });
    } finally {
      setClientLoading(false);
    }
  }, [loadClientErrors]);

  const clientSummary = useMemo(() => summarizeClientErrors(clientRows), [clientRows]);

  return (
    <div className="admin-page">
      <ReportsWorkspace session={session} />

      <div className="admin-card" style={{ marginTop: 18 }}>
        <div className="admin-row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <div>
            <div className="admin-section-title">Notification open errors</div>
            <div className="admin-muted">When a user taps a push and the match fails to load.</div>
          </div>
          <div className="admin-actions">
            <button className="admin-btn" type="button" onClick={loadClientErrors} disabled={clientLoading}>
              Refresh
            </button>
          </div>
        </div>

        <StatusPill msg={clientMsg} />

        <div className="admin-toolbar" style={{ marginTop: 12 }}>
          <label className="admin-label">
            Status
            <select className="admin-select" value={clientStatus} onChange={(e) => setClientStatus(e.target.value as any)}>
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="triaged">Triaged</option>
              <option value="closed">Closed</option>
            </select>
          </label>
          <label className="admin-label">
            Search
            <input className="admin-input" value={clientTerm} onChange={(e) => setClientTerm(e.target.value)} placeholder="id / user id / match id…" />
          </label>
        </div>

        <div className="admin-stack" style={{ marginTop: 12 }}>
          <div className="admin-muted">
            Loaded <span className="admin-mono">{clientRows.length}</span> event(s)
            {clientSummary.latestAt ? <> · latest {formatDate(clientSummary.latestAt.toISOString())}</> : null}
            {clientSummary.filters ? <> · {clientSummary.filters}</> : null}
          </div>
          <SummaryRow title="By app version" rows={clientSummary.byAppVersion} />
          <SummaryRow title="By update channel" rows={clientSummary.byChannel} />
          <SummaryRow title="By platform" rows={clientSummary.byPlatform} />
          <SummaryRow title="By notification type" rows={clientSummary.byNotificationType} />
          <SummaryRow title="By error code" rows={clientSummary.byErrorCode} />
        </div>

        <div className="admin-list" style={{ marginTop: 12 }}>
          {clientRows.length === 0 ? <div className="admin-muted">No notification errors.</div> : null}
          {clientRows.map((row) => (
            <div key={row.id} className="admin-list-item">
              <div className="admin-list-main">
                <div className="admin-row">
                  <div className="admin-mono">{row.id.slice(0, 8)}…</div>
                  <span className={clsx('admin-badge', row.status === 'open' && 'override')}>{row.status}</span>
                </div>
                <div className="admin-muted">
                  {formatDate(row.created_at)} · {row.platform ?? '—'} {row.app_version ? `v${row.app_version}` : ''}
                </div>
                <div className="admin-details-text">
                  <span className="admin-mono">{row.event_name}</span>
                  {row.match_id ? <span> · match {row.match_id.slice(0, 8)}…</span> : null}
                  {row.user_id ? <span> · user {row.user_id.slice(0, 8)}…</span> : null}
                </div>
                {row.error_message ? (
                  <div className="admin-details-text">
                    {row.error_message}
                    {row.error_code ? <span className="admin-mono"> ({row.error_code})</span> : null}
                  </div>
                ) : null}
                <details className="admin-details" style={{ marginTop: 10 }}>
                  <summary>Details</summary>
                  <pre className="admin-code" style={{ marginTop: 10 }}>
{stableStringify({
  route: row.route,
  notification_type: row.notification_type,
  notification_payload: row.notification_payload,
  params: row.params,
  build: row.build,
  error_stack: row.error_stack,
})}
                  </pre>
                </details>
              </div>
              <div className="admin-actions">
                <button className="admin-btn" type="button" disabled={clientLoading} onClick={() => updateClientError(row.id, 'triaged')}>Triaged</button>
                <button className="admin-btn" type="button" disabled={clientLoading} onClick={() => updateClientError(row.id, 'closed')}>Close</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

async function restFetch(session: Session, path: string, opts?: { withCount?: boolean }) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12_000);
  try {
    const headers: Record<string, string> = {
      apikey: config.supabaseAnonKey,
      authorization: `Bearer ${session.access_token}`,
    };
    if (opts?.withCount) headers.prefer = 'count=exact';
    const res = await fetch(`${config.supabaseUrl}/rest/v1/${path}`, { headers, signal: controller.signal });
    const body = await res.json().catch(() => []);
    if (!res.ok) {
      const message = (body as any)?.message || (body as any)?.error || `Request failed (${res.status})`;
      throw new Error(message);
    }
    const range = res.headers.get('content-range') || '';
    const total = range.includes('/') ? Number(range.split('/')[1]) : null;
    return { data: body as any, total: Number.isNaN(total) ? null : total };
  } finally {
    clearTimeout(timeout);
  }
}

async function fetchCount(session: Session, path: string) {
  const { total } = await restFetch(session, `${path}&limit=1`, { withCount: true });
  return total ?? 0;
}

async function fetchTimeline(session: Session, args: { table: string; column: string; days?: number; limit?: number }) {
  const days = args.days ?? 14;
  const limit = args.limit ?? 2000;
  const dayMs = 24 * 60 * 60 * 1000;
  const now = new Date();
  const start = new Date(now.getTime() - (days - 1) * dayMs);
  start.setHours(0, 0, 0, 0);
  const isoStart = start.toISOString();
  const { data, total } = await restFetch(
    session,
    `${args.table}?select=${args.column}&${args.column}=gte.${encodeURIComponent(isoStart)}&order=${args.column}.asc&limit=${limit}`,
    { withCount: true },
  );
  const series = Array.from({ length: days }, () => 0);
  (data ?? []).forEach((row: any) => {
    const value = row?.[args.column];
    if (!value) return;
    const ts = new Date(value).getTime();
    const idx = Math.floor((ts - start.getTime()) / dayMs);
    if (idx >= 0 && idx < days) series[idx] += 1;
  });
  return { series, start, total: total ?? null, sample: (data as any[])?.length ?? 0 };
}

function DashboardTab({ session }: { session: Session }) {
  const [msg, setMsg] = useState<StatusMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState<Record<string, number>>({});
  const [trendSignups, setTrendSignups] = useState<number[]>([]);
  const [trendJoins, setTrendJoins] = useState<number[]>([]);
  const [trendSessions, setTrendSessions] = useState<number[]>([]);
  const [recentUsers, setRecentUsers] = useState<Array<{ id: string; label: string; created_at: string }>>([]);
  const [recentSessions, setRecentSessions] = useState<Array<{ id: string; status: string; created_at: string }>>([]);
  const [recentMatches, setRecentMatches] = useState<Array<{ id: string; status: string; sport_id: string; created_at: string }>>([]);
  const [signals, setSignals] = useState<Array<{ label: string; value: string; meta: string }>>([]);
  const [insightsTab, setInsightsTab] = useState<'sports' | 'venues' | 'recentVenues' | 'areas'>('sports');
  const [insights, setInsights] = useState<{
    sports: Array<{ label: string; value: string; meta?: string }>;
    venues: Array<{ label: string; value: string; meta?: string }>;
    recentVenues: Array<{ label: string; value: string; meta?: string }>;
    areas: Array<{ label: string; value: string; meta?: string }>;
  }>({ sports: [], venues: [], recentVenues: [], areas: [] });
  const [funnel, setFunnel] = useState<{ base: number; rows: Array<{ label: string; value: number; meta?: string }> }>({ base: 0, rows: [] });

  const load = useCallback(async () => {
    const startedAt = performance.now();
    setLoading(true);
    setMsg({ tone: 'info', text: 'Loading dashboard…' });
    try {
      const result = await adminFetch<DashboardResponse>(session, { action: 'dashboard:get' });
      setKpis(result.kpis ?? {});
      setTrendSignups(Array.isArray(result.trendSignups) ? result.trendSignups : []);
      setTrendJoins(Array.isArray(result.trendJoins) ? result.trendJoins : []);
      setTrendSessions(Array.isArray(result.trendSessions) ? result.trendSessions : []);
      setRecentUsers(Array.isArray(result.recentUsers) ? result.recentUsers : []);
      setRecentSessions(Array.isArray(result.recentSessions) ? result.recentSessions : []);
      setRecentMatches(Array.isArray(result.recentMatches) ? result.recentMatches : []);
      setSignals(Array.isArray(result.signals) ? result.signals : []);
      setFunnel(result.funnel ?? { base: 0, rows: [] });
      setInsights(result.insights ?? { sports: [], venues: [], recentVenues: [], areas: [] });

      const warning = typeof result.warning === 'string' && result.warning.trim() ? result.warning.trim() : null;
      setMsg({ tone: warning ? 'warning' : 'success', text: `Updated ${new Date().toLocaleTimeString()}.${warning ? ` ${warning}` : ''}` });
      trackAdminEvent('admin_dashboard_view', {
        action: 'load',
        result: 'success',
        tab: 'dashboard',
        duration_ms: Math.round(performance.now() - startedAt),
        kpi_count: Object.keys(result.kpis ?? {}).length,
        recent_user_count: Array.isArray(result.recentUsers) ? result.recentUsers.length : 0,
      });
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to load dashboard.' });
      trackAdminEvent('admin_dashboard_view', {
        action: 'load',
        result: 'error',
        tab: 'dashboard',
        duration_ms: Math.round(performance.now() - startedAt),
      });
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">Dashboard</div>
          <div className="admin-subtitle">High-level metrics.</div>
        </div>
        <div className="admin-actions">
          <button
            className="admin-btn"
            type="button"
            onClick={() => {
              trackAdminEvent('admin_dashboard_refresh_click', { action: 'click', tab: 'dashboard' });
              load();
            }}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      <StatusPill msg={msg} />

      <div className="admin-grid-2" style={{ marginTop: 16 }}>
        <div className="admin-card">
          <div className="admin-section-title">Total users</div>
          <div className="admin-kpi">{formatNumber(kpis.totalUsers ?? 0)}</div>
          <div className="admin-muted">Lifetime profiles</div>
        </div>
        <div className="admin-card">
          <div className="admin-section-title">New users (7d)</div>
          <div className="admin-kpi">{formatNumber(kpis.newUsers7d ?? 0)}</div>
          <TrendDeltaChip series={trendSignups} />
          <MiniSparkline series={trendSignups} />
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Match joins (7d)</div>
          <div className="admin-kpi">{formatNumber(kpis.matchJoins7d ?? 0)}</div>
          <TrendDeltaChip series={trendJoins} />
          <MiniSparkline series={trendJoins} />
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Active players (30d)</div>
          <div className="admin-kpi">{formatNumber(kpis.activePlayers30dDistinct ?? 0)}</div>
          <div className="admin-muted">Distinct joiners (sampled if large)</div>
        </div>
      </div>

      <div className="admin-grid-2" style={{ marginTop: 16 }}>
        <div className="admin-card">
          <div className="admin-section-title">New users (30d)</div>
          <div className="admin-kpi">{formatNumber(kpis.newUsers30d ?? 0)}</div>
          <div className="admin-muted">Profiles created</div>
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Match joins (30d)</div>
          <div className="admin-kpi">{formatNumber(kpis.matchJoins30d ?? 0)}</div>
          <div className="admin-muted">Joined a match</div>
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Sessions (7d)</div>
          <div className="admin-kpi">{formatNumber(kpis.sessions7d ?? 0)}</div>
          <TrendDeltaChip series={trendSessions} />
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Independent logs (7d)</div>
          <div className="admin-kpi">{formatNumber(kpis.independentLogs7d ?? 0)}</div>
          <div className="admin-muted">Completed logs without a linked game</div>
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Active activity users (30d)</div>
          <div className="admin-kpi">{formatNumber(kpis.activeActivityUsers30d ?? 0)}</div>
          <div className="admin-muted">Game participants and independent loggers</div>
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Matches (7d)</div>
          <div className="admin-kpi">{formatNumber(kpis.matches7d ?? 0)}</div>
          <div className="admin-muted">Created matches</div>
        </div>
      </div>

      <div className="admin-grid-2" style={{ marginTop: 16 }}>
        <div className="admin-card">
          <div className="admin-section-title">Trends (14d)</div>
          <div className="admin-stack" style={{ marginTop: 12 }}>
            <div className="admin-trend-row">
              <div>
                <strong>Signups</strong>
                <div className="admin-muted">{formatNumber(sumValues(trendSignups))} total</div>
              </div>
              <TrendDeltaChip series={trendSignups} />
              <MiniSparkline series={trendSignups} />
            </div>
            <div className="admin-trend-row">
              <div>
                <strong>Match joins</strong>
                <div className="admin-muted">{formatNumber(sumValues(trendJoins))} total</div>
              </div>
              <TrendDeltaChip series={trendJoins} />
              <MiniSparkline series={trendJoins} />
            </div>
            <div className="admin-trend-row">
              <div>
                <strong>Sessions</strong>
                <div className="admin-muted">{formatNumber(sumValues(trendSessions))} total</div>
              </div>
              <TrendDeltaChip series={trendSessions} />
              <MiniSparkline series={trendSessions} />
            </div>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-section-title">Funnel</div>
          <div style={{ marginTop: 12 }}>
            <Funnel baseValue={funnel.base} rows={funnel.rows} />
          </div>
        </div>
      </div>

      <div className="admin-grid-2" style={{ marginTop: 16 }}>
        <div className="admin-card">
          <div className="admin-section-title">Platform signals</div>
          <div className="admin-insight-list" style={{ marginTop: 12 }}>
            {signals.length === 0 ? <div className="admin-muted">No signals yet.</div> : null}
            {signals.map((s) => (
              <div className="admin-insight-row" key={s.label}>
                <div>
                  <div className="admin-insight-label">{s.label}</div>
                  <div className="admin-muted">{s.meta}</div>
                </div>
                <div className="admin-insight-value">{s.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-section-title">Insights</div>
          <div style={{ marginTop: 10 }}>
            <InsightTabs active={insightsTab} onChange={(key) => setInsightsTab(key as any)} />
          </div>
          <div style={{ marginTop: 12 }}>
            <InsightList rows={insights[insightsTab] ?? []} />
          </div>
          <div className="admin-muted" style={{ marginTop: 10 }}>Based on the most recent 2,000 matches.</div>
        </div>
      </div>

      <div className="admin-grid-2" style={{ marginTop: 16 }}>
        <div className="admin-card">
          <div className="admin-section-title">Recent users</div>
          <div className="admin-list" style={{ marginTop: 12 }}>
            {recentUsers.length === 0 ? <div className="admin-muted">No recent users.</div> : null}
            {recentUsers.map((u) => (
              <div className="admin-list-item" key={u.id}>
                <div>
                  <div className="admin-row">
                    <strong>{u.label}</strong>
                    <span className="admin-badge mono">{u.id.slice(0, 6)}</span>
                  </div>
                  <div className="admin-muted">Joined {formatDate(u.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-section-title">Recent activity</div>
          <div className="admin-stack" style={{ marginTop: 12 }}>
            <div>
              <div className="admin-section-title" style={{ marginBottom: 8 }}>Sessions</div>
              <div className="admin-list">
                {recentSessions.length === 0 ? <div className="admin-muted">No recent sessions.</div> : null}
                {recentSessions.map((s) => (
                  <div className="admin-list-item" key={s.id}>
                    <div>
                      <div className="admin-row">
                        <strong>{s.status}</strong>
                        <span className="admin-badge mono">{s.id.slice(0, 6)}</span>
                      </div>
                      <div className="admin-muted">Created {formatDate(s.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="admin-section-title" style={{ marginBottom: 8 }}>Matches</div>
              <div className="admin-list">
                {recentMatches.length === 0 ? <div className="admin-muted">No recent matches.</div> : null}
                {recentMatches.map((m) => (
                  <div className="admin-list-item" key={m.id}>
                    <div>
                      <div className="admin-row">
                        <strong>{m.sport_id}</strong>
                        <span className="admin-badge">{m.status}</span>
                      </div>
                      <div className="admin-muted">Created {formatDate(m.created_at)} · <span className="admin-mono">{m.id.slice(0, 6)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatPercent(numerator: number, denominator: number) {
  if (!denominator) return '—';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

function sportLabel(value: string | null | undefined) {
  if (!value) return '—';
  return value
    .split(/[_-]+|\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function exportUsersCsv(rows: UserIntelRow[]) {
  const headers = [
    'id',
    'email',
    'username',
    'full_name',
    'created_at',
    'last_sign_in_at',
    'home_area',
    'preferred_activities',
    'profile_completeness',
    'value_stage',
    'created_games',
    'participated_games',
    'independent_logs',
    'unique_activities',
    'sessions',
    'last_created_game_at',
    'last_participated_game_at',
  ];
  const escapeCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((row) => [
    row.id,
    row.email,
    row.profile?.username,
    row.profile?.full_name,
    row.profile?.created_at,
    row.last_sign_in_at,
    row.homeArea ?? row.homeZip,
    row.preferredActivities.join('|'),
    row.profileCompleteness,
    row.valueStage,
    row.createdGamesCount,
    row.participatedGamesCount,
    row.independentLogsCount ?? 0,
    row.activityCount ?? 0,
    row.sessionCount,
    row.lastCreatedGameAt,
    row.lastParticipatedGameAt,
  ].map(escapeCell).join(','));
  const blob = new Blob([[headers.join(','), ...body].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `rvlry-user-intelligence-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function UserMatchList({ title, rows }: { title: string; rows: UserIntelMatch[] }) {
  return (
    <div>
      <div className="admin-section-title" style={{ marginBottom: 8 }}>{title}</div>
      <div className="admin-user-match-list">
        {rows.length === 0 ? <div className="admin-muted">None in sample.</div> : null}
        {rows.map((match) => (
          <div className="admin-user-match" key={`${title}-${match.id}-${match.joined_at ?? match.created_at ?? ''}`}>
            <div>
              <div className="admin-row">
                <strong>{sportLabel(match.sport_id)}</strong>
                {match.status ? <span className="admin-badge">{match.status}</span> : null}
              </div>
              <div className="admin-muted">
                {match.venue_name ?? 'No venue'} · {formatDate(match.joined_at ?? match.created_at)}
              </div>
            </div>
            <span className="admin-badge mono">{match.id.slice(0, 6)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function UserIntelCard({ user }: { user: UserIntelRow }) {
  const profile = user.profile ?? {};
  const displayName = profile.username || profile.full_name || user.email || user.id.slice(0, 8);
  const createdAt = typeof profile.created_at === 'string' ? profile.created_at : null;
  const galleryCount = Array.isArray(profile.gallery_urls) ? profile.gallery_urls.length : 0;
  const communityOpen = Boolean(user.communityStatus?.open);
  const activitySummary = user.preferredActivities.length === 0
    ? 'No activities'
    : user.preferredActivities.length === 1
      ? sportLabel(user.preferredActivities[0])
      : `${user.preferredActivities.length} activities`;

  return (
    <details className="admin-user-card">
      <summary className="admin-user-summary">
        <div className="admin-user-avatar">
          {typeof profile.avatar_url === 'string' && profile.avatar_url ? (
            <img src={profile.avatar_url} alt="" />
          ) : (
            <span>{String(displayName).slice(0, 1).toUpperCase()}</span>
          )}
        </div>
        <div className="admin-user-main">
          <div className="admin-row" style={{ gap: 8 }}>
            <strong>{displayName}</strong>
            <span className="admin-badge override">{user.valueStage}</span>
            {communityOpen ? <span className="admin-badge">Open to play</span> : null}
          </div>
          <div className="admin-muted">
            Joined {formatDate(createdAt)} · last seen {formatDate(user.last_sign_in_at)}
          </div>
          <div className="admin-user-chip-row">
            <span className="admin-chip">{user.profileCompleteness}% profile</span>
            <span className="admin-chip">{user.homeArea ?? (user.homeZip ? `ZIP ${user.homeZip}` : 'No area')}</span>
            <span className="admin-chip">{activitySummary}</span>
          </div>
        </div>
        <div className="admin-user-stats">
          <div><strong>{formatNumber(user.independentLogsCount ?? 0)}</strong><span>logged</span></div>
          <div><strong>{formatNumber(user.createdGamesCount)}</strong><span>games created</span></div>
          <div><strong>{formatNumber(user.participatedGamesCount)}</strong><span>joined</span></div>
          <div><strong>{formatNumber(user.sessionCount)}</strong><span>sessions</span></div>
        </div>
      </summary>
      <div className="admin-user-detail">
        <div className="admin-grid-2">
          <div className="admin-user-panel">
            <div className="admin-section-title">PM readout</div>
            <div className="admin-user-two-col">
              <div>
                <div className="admin-muted">Signals</div>
                <div className="admin-user-chip-row">
                  {user.valueSignals.map((signal) => <span className="admin-chip success" key={signal}>{signal}</span>)}
                </div>
              </div>
              <div>
                <div className="admin-muted">Questions</div>
                <ul className="admin-user-question-list">
                  {user.openQuestions.map((question) => <li key={question}>{question}</li>)}
                </ul>
              </div>
            </div>
            <div className="admin-user-field-grid" style={{ marginTop: 12 }}>
              <span>Community status</span><span>{communityOpen ? 'Open' : 'Closed / unset'}</span>
              <span>Location mode</span><span>{user.communityStatus?.location_kind ?? '—'}</span>
              <span>Location label</span><span>{user.communityStatus?.location_label ?? '—'}</span>
            </div>
          </div>
          <div className="admin-user-panel">
            <div className="admin-section-title">Profile</div>
            <div className="admin-user-field-grid">
              <span>ID</span><span className="admin-mono">{user.id}</span>
              <span>Email</span><span>{user.email ?? '—'}</span>
              <span>Name</span><span>{profile.full_name ?? '—'}</span>
              <span>First / last</span><span>{[profile.first_name, profile.last_name].filter(Boolean).join(' ') || '—'}</span>
              <span>Age / sex</span><span>{[profile.age, profile.sex].filter(Boolean).join(' · ') || '—'}</span>
              <span>Favorite</span><span>{sportLabel(profile.favorite_sport)}</span>
              <span>Location</span><span>{user.homeArea ?? (user.homeZip ? `ZIP ${user.homeZip}` : '—')}</span>
              <span>Provider</span><span>{user.sign_in_provider ?? '—'}</span>
              <span>Confirmed</span><span>{formatDate(user.confirmed_at)}</span>
              <span>Gallery</span><span>{galleryCount} image{galleryCount === 1 ? '' : 's'}</span>
            </div>
            {typeof profile.bio === 'string' && profile.bio.trim() ? (
              <div className="admin-details-text" style={{ marginTop: 12 }}>{profile.bio}</div>
            ) : null}
          </div>
        </div>
        <div className="admin-grid-2" style={{ marginTop: 12 }}>
          <UserMatchList title="Created games" rows={user.recentCreatedGames} />
          <UserMatchList title="Participated games" rows={user.recentParticipatedGames} />
        </div>
      </div>
    </details>
  );
}

function UsersTab({ session }: { session: Session }) {
  const [msg, setMsg] = useState<StatusMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [windowDays, setWindowDays] = useState(30);
  const [term, setTerm] = useState('');
  const [data, setData] = useState<UserIntelligenceValue | null>(null);

  const load = useCallback(async (source: 'initial' | 'refresh' | 'filter' = 'refresh') => {
    const startedAt = performance.now();
    setLoading(true);
    setMsg({ tone: 'info', text: 'Loading user intelligence…' });
    try {
      const result = await adminFetch<UserIntelligenceResponse>(session, {
        action: 'users:intelligence',
        windowDays,
        limit: 80,
        term,
      });
      const value = result.value;
      setData(value);
      const warning = typeof value?.warning === 'string' && value.warning.trim() ? value.warning.trim() : null;
      setMsg({ tone: warning ? 'warning' : 'success', text: `Loaded ${value?.users?.length ?? 0} users.${warning ? ` ${warning}` : ''}` });
      trackAdminEvent('admin_users_intelligence_view', {
        action: 'load',
        result: 'success',
        tab: 'users',
        source,
        duration_ms: Math.round(performance.now() - startedAt),
        row_count: value?.users?.length ?? 0,
        window_days: windowDays,
        has_search: term.trim().length > 0,
      });
    } catch (err) {
      setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to load user intelligence.' });
      trackAdminEvent('admin_users_intelligence_view', {
        action: 'load',
        result: 'error',
        tab: 'users',
        source,
        duration_ms: Math.round(performance.now() - startedAt),
      });
    } finally {
      setLoading(false);
    }
  }, [session, term, windowDays]);

  useEffect(() => {
    const startedAt = performance.now();
    setLoading(true);
    setMsg({ tone: 'info', text: 'Loading user intelligence…' });
    adminFetch<UserIntelligenceResponse>(session, {
      action: 'users:intelligence',
      windowDays: 30,
      limit: 80,
      term: '',
    })
      .then((result) => {
        const value = result.value;
        setData(value);
        const warning = typeof value?.warning === 'string' && value.warning.trim() ? value.warning.trim() : null;
        setMsg({ tone: warning ? 'warning' : 'success', text: `Loaded ${value?.users?.length ?? 0} users.${warning ? ` ${warning}` : ''}` });
        trackAdminEvent('admin_users_intelligence_view', {
          action: 'load',
          result: 'success',
          tab: 'users',
          source: 'initial',
          duration_ms: Math.round(performance.now() - startedAt),
          row_count: value?.users?.length ?? 0,
          window_days: 30,
          has_search: false,
        });
      })
      .catch((err) => {
        setMsg({ tone: 'error', text: (err as any)?.message ?? 'Failed to load user intelligence.' });
        trackAdminEvent('admin_users_intelligence_view', {
          action: 'load',
          result: 'error',
          tab: 'users',
          source: 'initial',
          duration_ms: Math.round(performance.now() - startedAt),
        });
      })
      .finally(() => setLoading(false));
  }, [session]);

  const overview = data?.overview ?? {};
  const users = data?.users ?? [];
  const profileCoverage = users.length ? formatPercent(users.filter((user) => user.profileCompleteness >= 67).length, users.length) : '—';
  const locationCoverage = users.length ? formatPercent(users.filter((user) => !!(user.homeArea ?? user.homeZip)).length, users.length) : '—';
  const activityCoverage = users.length ? formatPercent(users.filter((user) => user.preferredActivities.length > 0).length, users.length) : '—';

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">Users</div>
          <div className="admin-subtitle">Profile, location, activity intent, game participation, and value signals.</div>
        </div>
        <div className="admin-actions">
          <button className="admin-btn" type="button" onClick={() => load('refresh')} disabled={loading}>
            Refresh
          </button>
          <button
            className="admin-btn"
            type="button"
            onClick={() => {
              exportUsersCsv(users);
              trackAdminEvent('admin_users_export_click', { action: 'click', tab: 'users', row_count: users.length });
            }}
            disabled={!users.length}
          >
            Export CSV
          </button>
        </div>
      </div>

      <StatusPill msg={msg} />

      <div className="admin-toolbar" style={{ marginTop: 16 }}>
        <label className="admin-label">
          Signup window
          <select className="admin-select" value={windowDays} onChange={(event) => setWindowDays(Number(event.target.value))}>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>365 days</option>
          </select>
        </label>
        <label className="admin-label">
          Search
          <input className="admin-input" value={term} onChange={(event) => setTerm(event.target.value)} placeholder="username / name / area / user id…" />
        </label>
        <button className="admin-btn primary" type="button" onClick={() => load('filter')} disabled={loading}>
          Apply
        </button>
      </div>

      <div className="admin-grid-2 admin-user-kpi-grid" style={{ marginTop: 16 }}>
        <div className="admin-card">
          <div className="admin-section-title">New users (30d)</div>
          <div className="admin-kpi">{formatNumber(overview.newUsers30d ?? 0)}</div>
          <div className="admin-muted">{formatNumber(overview.newUsers7d ?? 0)} in the last 7 days</div>
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Activation</div>
          <div className="admin-kpi">{formatPercent(overview.activatedNewUsers30d ?? 0, overview.sampledNewUsers30d ?? overview.newUsers30d ?? 0)}</div>
          <div className="admin-muted">{formatNumber(overview.activatedNewUsers30d ?? 0)} sampled new users created, joined or logged activity</div>
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Profile coverage</div>
          <div className="admin-kpi">{profileCoverage}</div>
          <div className="admin-muted">{activityCoverage} activity intent · {locationCoverage} area coverage</div>
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Creator / joiner health</div>
          <div className="admin-kpi">{formatNumber(overview.usersCreatedGame30d ?? 0)} / {formatNumber(overview.usersJoinedGame30d ?? 0)}</div>
          <div className="admin-muted">30d creators / participants · {formatNumber(overview.usersReturned30d ?? 0)} returning</div>
        </div>
      </div>

      <div className="admin-grid-2" style={{ marginTop: 16 }}>
        <div className="admin-card">
          <div className="admin-section-title">CPO questions</div>
          <div className="admin-insight-list" style={{ marginTop: 12 }}>
            {(data?.questions ?? []).map((question) => (
              <div className="admin-insight-row" key={question.label}>
                <div>
                  <div className="admin-insight-label">{question.label}</div>
                  <div className="admin-muted">{question.meta}</div>
                </div>
                <div className="admin-insight-value">{question.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Value stages</div>
          <InsightList rows={data?.insights.valueStages ?? []} />
          <div className="admin-muted" style={{ marginTop: 10 }}>Classifies sampled users by retained, creator, participant, or not activated.</div>
        </div>
      </div>

      <div className="admin-grid-2" style={{ marginTop: 16 }}>
        <div className="admin-card">
          <div className="admin-section-title">What they want</div>
          <InsightList rows={(data?.insights.preferredActivities ?? []).map((row) => ({ ...row, label: sportLabel(row.label) }))} />
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Where they are</div>
          <InsightList rows={data?.insights.zipCodes ?? []} />
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Created game sports</div>
          <InsightList rows={(data?.insights.createdSports ?? []).map((row) => ({ ...row, label: sportLabel(row.label) }))} />
        </div>
        <div className="admin-card">
          <div className="admin-section-title">Participated game sports</div>
          <InsightList rows={(data?.insights.participatedSports ?? []).map((row) => ({ ...row, label: sportLabel(row.label) }))} />
        </div>
      </div>

      <div className="admin-card" style={{ marginTop: 16 }}>
        <div className="admin-row" style={{ justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div className="admin-section-title">New user profiles</div>
            <div className="admin-muted">Expand a row to inspect profile fields, location, preferred activities, game creation, and game participation.</div>
          </div>
          <div className="admin-badge mono">{users.length} users</div>
        </div>
        <div className="admin-user-list">
          {users.length === 0 ? <div className="admin-empty">No users match this window.</div> : null}
          {users.map((user) => <UserIntelCard key={user.id} user={user} />)}
        </div>
      </div>
    </div>
  );
}

function OnboardingTipsCMS({ session }: { session: Session }) {
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState<{ updated_at: string | null; updated_by: string | null } | null>(null);

  const [baseline, setBaseline] = useState<OnboardingTipsSettings>(DEFAULT_ONBOARDING_TIPS_SETTINGS);
  const [draft, setDraft] = useState<OnboardingTipsSettings>(DEFAULT_ONBOARDING_TIPS_SETTINGS);

  const [search, setSearch] = useState('');
  const [surfaceFilter, setSurfaceFilter] = useState<'all' | string>('all');
  const [audienceFilter, setAudienceFilter] = useState<'all' | string>('all');

  const dirty = useMemo(() => stableStringify(baseline) !== stableStringify(draft), [baseline, draft]);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus({ tone: 'info', text: 'Loading…' });
    try {
      const result = await adminFetch<{ value: any }>(session, { action: 'onboarding_tips:get' });
      const stored = isObject(result.value) ? (result.value as Record<string, unknown>) : {};
      const normalized = deepMerge(DEFAULT_ONBOARDING_TIPS_SETTINGS, stored) as OnboardingTipsSettings;
      setBaseline(normalized);
      setDraft(JSON.parse(JSON.stringify(normalized)) as OnboardingTipsSettings);
      setMeta({
        updated_at: (result.settings as any)?.updated_at ?? null,
        updated_by: (result.settings as any)?.updated_by ?? null,
      });
      const updatedAt = (result.settings as any)?.updated_at ? formatDate((result.settings as any)?.updated_at) : null;
      const updatedBy = (result.settings as any)?.updated_by ? String((result.settings as any)?.updated_by).slice(0, 8) + '…' : null;
      setStatus({ tone: 'success', text: updatedAt ? `Loaded. Last updated ${updatedAt}${updatedBy ? ` by ${updatedBy}` : ''}.` : 'Loaded (no row found).' });
    } catch (err) {
      setStatus({ tone: 'error', text: (err as any)?.message ?? 'Failed to load onboarding tips.' });
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setLoading(true);
    setStatus({ tone: 'info', text: 'Saving…' });
    try {
      await adminFetch(session, { action: 'onboarding_tips:save', value: draft });
      await load();
      setStatus({ tone: 'success', text: 'Saved.' });
    } catch (err) {
      setStatus({ tone: 'error', text: (err as any)?.message ?? 'Failed to save.' });
    } finally {
      setLoading(false);
    }
  }, [session, draft, load]);

  const reset = useCallback(() => {
    setDraft(JSON.parse(JSON.stringify(DEFAULT_ONBOARDING_TIPS_SETTINGS)));
    setStatus({ tone: 'success', text: 'Reset to defaults (not saved yet).' });
  }, []);

  const addTip = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      tips: [
        ...prev.tips,
        {
          id: '',
          surface: 'match_lobby',
          audience: 'any',
          placement: 'inline',
          enabled: true,
          title: '',
          subtitle: '',
          cta: 'Got it',
          maxShows: 1,
        },
      ],
    }));
  }, []);

  const updateTip = useCallback((index: number, patch: Partial<OnboardingTipRow>) => {
    setDraft((prev) => {
      const tips = [...prev.tips];
      tips[index] = { ...tips[index], ...patch };
      return { ...prev, tips };
    });
  }, []);

  const deleteTip = useCallback((index: number) => {
    setDraft((prev) => {
      const tips = [...prev.tips];
      tips.splice(index, 1);
      return { ...prev, tips };
    });
  }, []);

  const surfaces = useMemo(() => {
    const set = new Set<string>();
    draft.tips.forEach((t) => {
      const s = (t.surface ?? '').trim();
      if (s) set.add(s);
    });
    return Array.from(set).sort();
  }, [draft.tips]);

  const audiences = useMemo(() => {
    const set = new Set<string>();
    draft.tips.forEach((t) => {
      const s = (t.audience ?? '').trim();
      if (s) set.add(s);
    });
    return Array.from(set).sort();
  }, [draft.tips]);

  const filteredTips = useMemo(() => {
    const term = search.trim().toLowerCase();
    return draft.tips
      .map((tip, index) => ({ tip, index }))
      .filter(({ tip }) => {
        if (surfaceFilter !== 'all' && (tip.surface ?? '') !== surfaceFilter) return false;
        if (audienceFilter !== 'all' && (tip.audience ?? '') !== audienceFilter) return false;
        if (!term) return true;
        const hay = [tip.id, tip.title, tip.subtitle, tip.cta, tip.surface, tip.audience, tip.placement].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(term);
      });
  }, [draft.tips, search, surfaceFilter, audienceFilter]);

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">Onboarding tips</div>
          <div className="admin-subtitle">Lightweight tips for first-time moments. Turn on when ready.</div>
          <div className="admin-muted" style={{ marginTop: 6 }}>
            Note: <span className="admin-mono">play.map.discovery</span> renders as the Play map toast with a dynamic sport label in the title.
          </div>
          {meta?.updated_at ? (
            <div className="admin-muted" style={{ marginTop: 6 }}>
              Last update: <span className="admin-mono">{formatDate(meta.updated_at)}</span>
            </div>
          ) : null}
        </div>
        <div className="admin-actions">
          <label className="admin-check" style={{ gap: 10, paddingRight: 6 }}>
            <input
              type="checkbox"
              checked={Boolean(draft.enabled)}
              onChange={(e) => setDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            Enabled
          </label>
          <button className="admin-btn" type="button" onClick={reset} disabled={loading}>Reset</button>
          <button className="admin-btn" type="button" onClick={addTip} disabled={loading}>Add tip</button>
          <button className={clsx('admin-btn primary')} type="button" onClick={save} disabled={loading || !dirty}>Save</button>
        </div>
      </div>

      <StatusPill msg={status} />

      <div className="admin-toolbar">
        <label className="admin-label" style={{ flex: 1 }}>
          Search
          <input className="admin-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find by id, surface, or text…" />
        </label>
        <label className="admin-label">
          Surface
          <select className="admin-select" value={surfaceFilter} onChange={(e) => setSurfaceFilter(e.target.value)}>
            <option value="all">All</option>
            {surfaces.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="admin-label">
          Audience
          <select className="admin-select" value={audienceFilter} onChange={(e) => setAudienceFilter(e.target.value)}>
            <option value="all">All</option>
            {audiences.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <div className="admin-tip-list" role="list">
        {filteredTips.map(({ tip, index }) => {
          const hasId = tip.id.trim().length > 0;
          return (
            <div key={`${tip.id || 'new'}-${index}`} className={clsx('admin-tip-row', !hasId && 'invalid')} role="listitem">
              <div className="admin-tip-row-head">
                <label className="admin-check">
                  <input
                    type="checkbox"
                    checked={Boolean(tip.enabled ?? true)}
                    onChange={(e) => updateTip(index, { enabled: e.target.checked })}
                  />
                  Enabled
                </label>
                <div className="admin-tip-meta">
                  <span className="admin-tip-id">{tip.id || '(missing id)'}</span>
                  <span className="admin-tip-chip">{tip.surface || 'surface'}</span>
                  <span className="admin-tip-chip">{tip.audience || 'audience'}</span>
                </div>
                <button className="admin-btn danger small" type="button" onClick={() => deleteTip(index)} disabled={loading}>
                  Delete
                </button>
              </div>

              <div className="admin-tip-grid">
                <label className="admin-label">
                  Tip id
                  <input className="admin-input" value={tip.id} onChange={(e) => updateTip(index, { id: e.target.value })} placeholder="match.lobby.controller.set_teams" />
                </label>
                <label className="admin-label">
                  Surface
                  <input className="admin-input" value={tip.surface ?? ''} onChange={(e) => updateTip(index, { surface: e.target.value })} placeholder="match_lobby" />
                </label>
                <label className="admin-label">
                  Audience
                  <input className="admin-input" value={tip.audience ?? ''} onChange={(e) => updateTip(index, { audience: e.target.value })} placeholder="controller / participant / any" />
                </label>
                <label className="admin-label">
                  Placement
                  <input className="admin-input" value={tip.placement ?? ''} onChange={(e) => updateTip(index, { placement: e.target.value })} placeholder="inline.set_teams" />
                </label>
              </div>

              <div className="admin-tip-grid" style={{ marginTop: 8 }}>
                <label className="admin-label">
                  Title
                  <input className="admin-input" value={tip.title ?? ''} onChange={(e) => updateTip(index, { title: e.target.value })} placeholder="SET TEAMS" />
                </label>
                <label className="admin-label" style={{ gridColumn: 'span 2' }}>
                  Subtitle
                  <input className="admin-input" value={tip.subtitle ?? ''} onChange={(e) => updateTip(index, { subtitle: e.target.value })} placeholder="Add 1+ to Light and Dark to start." />
                </label>
                <label className="admin-label">
                  CTA
                  <input className="admin-input" value={tip.cta ?? ''} onChange={(e) => updateTip(index, { cta: e.target.value })} placeholder="Got it" />
                </label>
              </div>

              <div className="admin-tip-grid" style={{ marginTop: 8, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                <label className="admin-label">
                  Max shows
                  <input
                    className="admin-input"
                    inputMode="numeric"
                    value={String(tip.maxShows ?? 1)}
                    onChange={(e) => updateTip(index, { maxShows: Number(e.target.value) })}
                    placeholder="1"
                  />
                </label>
                <label className="admin-label" style={{ gridColumn: 'span 3' }}>
                  Notes
                  <div className="admin-muted" style={{ marginTop: 8 }}>
                    Keep title short (1–2 words) and subtitle under ~2 lines on mobile.
                  </div>
                </label>
              </div>
            </div>
          );
        })}

        {filteredTips.length === 0 ? (
          <div className="admin-empty">No tips match your filters.</div>
        ) : null}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { session, loading } = useAdminSession();
  const { isAdmin, loading: loadingAdmin } = useIsAdmin(session);
  const [search, setSearch] = useSearchParams();
  const tab = useMemo(() => resolveTab(search), [search]);
  const initialTabRef = useRef<TabKey | null>(null);

  useEffect(() => {
    if (initialTabRef.current) return;
    initialTabRef.current = tab;
  }, [tab]);

  useAdminClickAutocapture(tab, Boolean(session && isAdmin));

  useEffect(() => {
    if (!session || !isAdmin) return;
    identifyAdminUser(session);
  }, [isAdmin, session]);

  useEffect(() => {
    if (!session || !isAdmin) return;
    trackAdminEvent('admin_page_view', {
      action: 'view',
      tab,
      page: tab,
      user_role: 'admin',
    });
  }, [isAdmin, session, tab]);

  const setTab = useCallback((next: TabKey) => {
    const copy = new URLSearchParams(search);
    copy.set('tab', next);
    copy.delete('cms');
    setSearch(copy, { replace: true });
  }, [search, setSearch]);

  if (loading) {
    return (
      <div className="admin-shell">
        <main className="admin-auth">
          <div className="admin-card">Loading…</div>
        </main>
      </div>
    );
  }

  if (!session) return <AuthCard />;

  if (loadingAdmin || isAdmin === null) {
    return (
      <div className="admin-shell">
        <main className="admin-auth">
          <div className="admin-card">Checking access…</div>
        </main>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="admin-shell">
        <main className="admin-auth">
          <div className="admin-card">
            <div className="admin-title">Not authorized</div>
            <div className="admin-subtitle">
              Your account is signed in, but not present in <span className="admin-mono">app_admins</span>.
            </div>
            <div className="admin-actions" style={{ marginTop: 16 }}>
              <button className="admin-btn danger" type="button" onClick={() => supabase.auth.signOut()}>
                Log out
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <AdminLayout session={session} activeTab={tab} onTab={setTab}>
      {tab === 'cms' ? <CmsWorkspace session={session} /> : null}
      {tab === 'events' ? <EventsWorkspace session={session} /> : null}
      {tab === 'match_copy' ? <MatchCopyWorkspace session={session} /> : null}
      {tab === 'location_copy' ? <LocationCopyWorkspace session={session} /> : null}
      {tab === 'play_mode_copy' ? <PlayModeCopyWorkspace session={session} /> : null}
      {tab === 'sports' ? <SportsTab session={session} /> : null}
      {tab === 'sport_audit' ? <SportFormatAuditWorkspace session={session} /> : null}
      {tab === 'onboarding' ? <OnboardingTipsCMS session={session} /> : null}
      {tab === 'settings' ? <SettingsTab session={session} /> : null}
      {tab === 'reports' ? <ReportsTab session={session} /> : null}
      {tab === 'users' ? <UsersTab session={session} /> : null}
      {tab === 'dashboard' ? <DashboardTab session={session} /> : null}
    </AdminLayout>
  );
}
